import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  Box, Container, Typography, Button, Grid,
  AppBar, Toolbar, IconButton,
  Dialog, DialogContent, Select, MenuItem, ListSubheader,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowRight, Menu as MenuIcon, X, Sparkles,
  CheckCircle2, Building2, Check, Calendar,
  ChevronLeft, ChevronRight,
  Database, Zap, Link2, Bell, Users, Smartphone, Monitor,
  BarChart2, PieChart, Eye, BookOpen, Sliders, Shield, Rocket,
  User, GraduationCap, Star,
} from 'lucide-react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ROUTES } from '@/router/routes';
import ExploreSection from './components/ExploreSection';

// ─── HOME PAGE THEME (LIGHT MODE) ────────────────────────────────────────────
const homeTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#7C63D8', light: '#9B85E2', dark: '#6B52D1', contrastText: '#FFFFFF' },
    secondary: { main: '#F59E0B', light: '#FBB944', dark: '#D97706', contrastText: '#000000' },
    background: { default: '#F8FAFC', paper: '#FFFFFF' },
    text: { primary: '#111827', secondary: '#475569', disabled: '#94A3B8' },
    success: { main: '#34D399' },
    error: { main: '#F87171' },
    divider: '#E5E7EB',
  },
  typography: {
    fontFamily: '"DM Sans", sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: { fontFamily: '"Syne", sans-serif', fontWeight: 800, lineHeight: 1.08, letterSpacing: '-2px' },
    h2: { fontFamily: '"Syne", sans-serif', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-1px' },
    h3: { fontFamily: '"Syne", sans-serif', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.3px' },
    h4: { fontFamily: '"Syne", sans-serif', fontWeight: 700, lineHeight: 1.4 },
    body1: { fontSize: '1rem', lineHeight: 1.7, fontWeight: 300 },
    body2: { fontSize: '0.875rem', lineHeight: 1.65, fontWeight: 400 },
    button: { fontWeight: 500, textTransform: 'none' as const },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: { styleOverrides: { root: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } } } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none', boxShadow: 'none' } } },
    MuiCard: { styleOverrides: { root: { backgroundImage: 'none', boxShadow: 'none' } } },
    MuiAppBar: { styleOverrides: { root: { backgroundImage: 'none' } } },
  },
});

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const SHOW_TESTIMONIALS = true;

const NAV_LINKS = [
  { label: 'Modules', href: '#modules' },
  { label: 'Experiences', href: '#roles' },
  { label: 'Pricing & Partnership', href: '#partnership' },
  ...(SHOW_TESTIMONIALS ? [{ label: 'Testimonials', href: '#testimonials' }] : []),
];

const HOME_TICKER = [
  'Fee collection synced', 'Attendance updated', 'Academic reports ready',
  'Performance insights live', 'Parent notifications sent', 'Leadership dashboard active',
  'Exam data processed', 'Staff schedules aligned', 'Analytics refreshed',
];

// ─── DATA ────────────────────────────────────────────────────────────────────
const ROLE_SANDBOX = [
  {
    id: 'administrators',
    label: 'Administrators',
    desc: 'Full operational control',
    icon: '⚙️',
    user: 'Priya Sharma',
    initials: 'PS',
    avatarColor: '#4f46e5',
    sidebarItems: ['Dashboard', 'Students', 'Fee Collection', 'Settings'],
    widgets: [
      { icon: '🎓', label: 'Total Students', value: '1,248', bg: '#e0f2fe', color: '#0284c7' },
      { icon: '👨‍🏫', label: 'Staff Count', value: '86', bg: '#f3f0ff', color: '#7C63D8' },
      { icon: '💳', label: 'Fees Collected', value: '₹8.2L', bg: '#d1fae5', color: '#059669' },
    ],
    chartBars: [60, 80, 72, 90, 65],
    chartLabel: 'Monthly Fee Collection',
    listTitle: 'Pending Approvals',
    listItems: [
      { label: 'Leave Request — Arun K.', badge: 'Pending', badgeType: 'amber' },
      { label: 'Fee Waiver — Class 10B', badge: 'Review', badgeType: 'amber' },
      { label: 'Payroll Processed', badge: 'Done', badgeType: 'green' },
    ],
  },
  {
    id: 'teachers',
    label: 'Teachers',
    desc: 'Classes & assessments',
    icon: '📚',
    user: 'Rahul Mehta',
    initials: 'RM',
    avatarColor: '#10b981',
    sidebarItems: ['Classes', 'Attendance', 'Exams', 'Syllabus'],
    widgets: [
      { icon: '📖', label: 'Subjects Assigned', value: '4', bg: '#e0f2fe', color: '#0284c7' },
      { icon: '✅', label: 'Attendance Today', value: '94%', bg: '#d1fae5', color: '#059669' },
      { icon: '📝', label: 'Pending Assessments', value: '12', bg: '#fff7ed', color: '#ea580c' },
    ],
    chartBars: [90, 75, 60, 85, 70],
    chartLabel: 'Syllabus Completion',
    listTitle: "Today's Schedule",
    listItems: [
      { label: 'Physics — Grade 10', badge: '9:00 AM', badgeType: 'slate' },
      { label: 'Lab Session — Grade 12', badge: '11:30 AM', badgeType: 'slate' },
      { label: 'Staff Meeting', badge: '3:30 PM', badgeType: 'amber' },
    ],
  },
  {
    id: 'management',
    label: 'Management',
    desc: 'Institution-wide view',
    icon: '📊',
    user: 'Vikram Nair',
    initials: 'VN',
    avatarColor: '#7C63D8',
    sidebarItems: ['Overview', 'Analytics', 'Reports', 'Governance'],
    widgets: [
      { icon: '🏫', label: 'Active Campuses', value: '3', bg: '#f3f0ff', color: '#7C63D8' },
      { icon: '👩‍🎓', label: 'Total Students', value: '4,820', bg: '#e0f2fe', color: '#0284c7' },
      { icon: '📈', label: 'Satisfaction Rate', value: '98%', bg: '#d1fae5', color: '#059669' },
    ],
    chartBars: [75, 88, 92, 85, 95],
    chartLabel: 'Campus Performance',
    listTitle: 'Leadership Alerts',
    listItems: [
      { label: 'Q2 Report Generated', badge: 'Ready', badgeType: 'green' },
      { label: 'New Campus Onboarding', badge: 'Active', badgeType: 'green' },
      { label: 'Compliance Audit Due', badge: 'Soon', badgeType: 'amber' },
    ],
  },
  {
    id: 'families',
    label: 'Parents & Students',
    desc: 'Progress & communication',
    icon: '👨‍👩‍👧',
    user: 'Amit Kumar',
    initials: 'AK',
    avatarColor: '#f59e0b',
    sidebarItems: ['My Progress', 'Timetable', 'Fee Pay', 'Messages'],
    widgets: [
      { icon: '📅', label: 'Attendance', value: '97%', bg: '#d1fae5', color: '#059669' },
      { icon: '🏆', label: 'Class Rank', value: '#2', bg: '#fff7ed', color: '#ea580c' },
      { icon: '💳', label: 'Fee Status', value: 'Paid', bg: '#f0fdf4', color: '#16a34a' },
    ],
    chartBars: [88, 92, 78, 95, 85],
    chartLabel: 'Subject Performance',
    listTitle: 'Upcoming Events',
    listItems: [
      { label: 'Term Exam — Physics', badge: 'Jun 14', badgeType: 'amber' },
      { label: 'Sports Day', badge: 'Jun 20', badgeType: 'green' },
      { label: 'Parent-Teacher Meet', badge: 'Jul 2', badgeType: 'slate' },
    ],
  },
];

const VISION_PILLARS = [
  { num: '01', title: 'Seamless Information Flow', body: 'Every department, campus, and stakeholder connected through a single source of institutional truth — available in real time, always.' },
  { num: '02', title: 'Stakeholders Who Feel Seen', body: 'Parents engaged, teachers supported, students motivated — because every interaction is personalized, timely, and meaningful.' },
  { num: '03', title: 'Leaders Who See Everything', body: 'Complete institutional visibility at a glance — so leadership can act on opportunities and address challenges before they become problems.' },
  { num: '04', title: 'Decisions That Move Fast', body: 'Powered by intelligence, not instinct — your institution responds to real-world changes with speed and confidence.' },
  { num: '05', title: 'Operations That Expand Naturally', body: 'From one campus to ten, the same ecosystem scales with you — without rebuilding, retraining, or starting over.' },
];

const PLATFORM_CARDS = [
  {
    num: '01',
    title: 'Everything Updates Instantly',
    body: 'Admissions, attendance, academics and fees stay perfectly synchronized across your institution.',
    features: [
      { Icon: Database, label: 'One Source of Truth' },
      { Icon: Zap, label: 'Live Data' },
      { Icon: Link2, label: 'Zero Duplication' },
    ],
  },
  {
    num: '02',
    title: 'Parents Stay Connected',
    body: 'Homework, attendance, fee reminders and notices reach parents instantly through one app.',
    features: [
      { Icon: Bell, label: 'Instant Updates' },
      { Icon: Users, label: 'Better Engagement' },
      { Icon: Smartphone, label: 'Always Informed' },
    ],
  },
  {
    num: '03',
    title: 'Make Faster Decisions',
    body: 'Real-time insights help principals and management make faster, smarter decisions.',
    features: [
      { Icon: BarChart2, label: 'Live Reports' },
      { Icon: PieChart, label: 'Smart Insights' },
      { Icon: Eye, label: 'Complete Visibility' },
    ],
  },
  {
    num: '04',
    title: 'Less Administration. More Teaching.',
    body: 'Automate daily tasks and give teachers more time to focus on what matters most — students.',
    features: [
      { Icon: CheckCircle2, label: 'Quick Attendance' },
      { Icon: BookOpen, label: 'Easy Homework' },
      { Icon: Sliders, label: 'Streamlined Workflows' },
    ],
  },
  {
    num: '05',
    title: 'Built to Grow',
    body: 'From one campus to many, SkolarMinds scales effortlessly without changing systems.',
    features: [
      { Icon: Building2, label: 'Multi-Campus Ready' },
      { Icon: Shield, label: 'Secure Cloud Platform' },
      { Icon: Rocket, label: 'Future-Ready Architecture' },
    ],
  },
];

const MODULE_CATEGORIES = [
  {
    id: 'academic', label: 'Academic',
    modules: [
      { icon: '🎓', title: 'Student Management', lead: 'Know every student\'s journey — from first inquiry to alumni milestone.' },
      { icon: '📋', title: 'Attendance', lead: 'Turn attendance data into early intelligence for student success.' },
      { icon: '📚', title: 'Syllabus', lead: 'Keep your academic program aligned, consistent, and always on track.' },
      { icon: '🗓️', title: 'Timetable', lead: 'Build schedules that optimise teaching, space, and learning outcomes.' },
      { icon: '📝', title: 'Exam & Assessment', lead: 'Structured evaluations with transparent results that families can trust.' },
      { icon: '🖥️', title: 'Online Classes', lead: 'Extend the boundaries of your institution to wherever learning needs to happen.' },
    ],
  },
  {
    id: 'finance', label: 'Finance',
    modules: [
      { icon: '💳', title: 'Fee Management', lead: 'Create a frictionless financial experience for families and institutions alike.' },
      { icon: '💼', title: 'Expense Management', lead: 'Bring financial discipline and transparency to every rupee your institution spends.' },
      { icon: '💰', title: 'Salary & Payroll', lead: 'Build a payroll process your team trusts and your finance team loves.' },
    ],
  },
  {
    id: 'hr', label: 'HR & People',
    modules: [
      { icon: '👨‍🏫', title: 'Employee Management', lead: 'Build a high-performing team by investing in people, not paperwork.' },
      { icon: '💬', title: 'Communication', lead: 'Replace fragmented messaging with a communication platform built for institutions.' },
      { icon: '🏖️', title: 'Leaves & Holidays', lead: 'Build a fair, transparent leave culture that respects everyone\'s time.' },
    ],
  },
  {
    id: 'operations', label: 'Operations',
    modules: [
      { icon: '📖', title: 'Library', lead: 'Transform your library from a room into an active engine for learning.' },
      { icon: '🏠', title: 'Hostel', lead: 'Give residential students a safe, well-managed home away from home.' },
      { icon: '🚌', title: 'Transport', lead: 'Build a transport ecosystem that keeps students safe and parents confident.' },
      { icon: '🎉', title: 'Events', lead: 'Make every institutional event an experience that builds community and reputation.' },
      { icon: '📦', title: 'Inventory', lead: 'Ensure your institution always has what it needs, exactly when it needs it.' },
      { icon: '🎫', title: 'Ticketing & Support', lead: 'Every concern raised, tracked, and resolved — nothing slips through the cracks.' },
    ],
  },
  {
    id: 'intelligence', label: 'Intelligence',
    modules: [
      { icon: '📊', title: 'Reports & Analytics', lead: 'Gain complete visibility into your institution with powerful, real-time reporting.', featured: true },
      { icon: '📱', title: 'Role Dashboards', lead: 'Every stakeholder sees exactly what they need to do their best work.' },
    ],
  },
];

const ROLES = [
  {
    id: 'management',
    label: 'Management',
    title: 'Leadership That Leads with Clarity',
    body: 'Management and trustees gain a bird\'s eye view of their entire institution — every campus, every department, every metric. Strategic decisions become faster, more confident, and more consistently right.',
    features: [
      { icon: '📊', title: 'Institutional Dashboards', text: 'Real-time health metrics across all campuses in one view.' },
      { icon: '📈', title: 'Financial Visibility', text: 'Revenue and spending trends without waiting for manual reports.' },
      { icon: '🎯', title: 'Governance Reporting', text: 'Board and compliance reports generated automatically.' },
      { icon: '🌐', title: 'Multi-Campus Comparison', text: 'Spot best practices and operational gaps across every campus.' },
      { icon: '🔔', title: 'Proactive Alerts', text: 'Issues surface before they escalate into larger problems.' },
      { icon: '📋', title: 'Audit-Ready Records', text: 'Regulatory documentation always current and always accessible.' },
    ],
  },
  {
    id: 'admin',
    label: 'Administrators',
    title: 'Administration That Operates at Its Best',
    body: 'Administrators gain the tools and clarity to run operations smoothly — with less effort, fewer errors, and far greater confidence that nothing is falling through the cracks.',
    features: [
      { icon: '⚙️', title: 'Workflow Automation', text: 'Repetitive manual tasks handled through structured, automated flows.' },
      { icon: '📁', title: 'Centralised Records', text: 'Every file and document accessible whenever and wherever needed.' },
      { icon: '🔔', title: 'Intelligent Alerts', text: 'Potential issues flagged early — before they become operational problems.' },
      { icon: '📋', title: 'Compliance Documentation', text: 'Compliance-ready reports generated without manual compilation.' },
      { icon: '📊', title: 'Operational Reports', text: 'Clear visibility across departments without chasing data.' },
      { icon: '🔗', title: 'Cross-Department Coordination', text: 'Teams stay aligned through connected, shared workflows.' },
    ],
  },
  {
    id: 'teachers',
    label: 'Teachers',
    title: 'Teachers Who Can Focus on Teaching',
    body: 'When administrative burden lifts, teachers reclaim what matters most — the space to understand each student deeply and deliver education that genuinely transforms.',
    features: [
      { icon: '📚', title: 'Student Insights', text: 'Progress and attendance data that helps identify who needs support.' },
      { icon: '✅', title: 'Digital Attendance', text: 'Mark and track attendance in minutes, not hours.' },
      { icon: '💬', title: 'Parent Communication', text: 'Direct messaging channels that build genuine partnership with families.' },
      { icon: '🎯', title: 'Syllabus Tracking', text: 'Keep the entire class moving together with visual curriculum progress.' },
      { icon: '📝', title: 'Assessment Tools', text: 'Create, publish, and review assessments without administrative overhead.' },
      { icon: '🗓️', title: 'Schedule Clarity', text: 'Timetables, substitutions, and exam schedules always up to date.' },
    ],
  },
  {
    id: 'families',
    label: 'Parents & Students',
    title: 'Parents and Students Who Stay Connected',
    body: 'Parents get clear visibility into their child\'s academic life. Students get the tools to stay organised and in control of their own journey — so the whole family feels informed and involved.',
    features: [
      { icon: '📱', title: 'Academic Progress', text: 'Live updates on grades, attendance, and performance.', tag: 'Parent' },
      { icon: '🚌', title: 'Transport Tracking', text: 'Visibility into pickup and drop-off for daily safety confidence.', tag: 'Parent' },
      { icon: '💳', title: 'Fee Payments', text: 'Online payments with complete transaction history and receipts.', tag: 'Parent' },
      { icon: '📣', title: 'Direct Communication', text: 'Message teachers and administration without going through intermediaries.', tag: 'Parent' },
      { icon: '🎓', title: 'Personal Dashboard', text: 'Attendance, timetable, assignments, and performance in one view.', tag: 'Student' },
      { icon: '📖', title: 'Learning Resources', text: 'Notes, materials, and class content accessible anytime.', tag: 'Student' },
      { icon: '📅', title: 'Exam Schedule', text: 'Exam dates and syllabus coverage visible well in advance.', tag: 'Student' },
      { icon: '🏆', title: 'Achievement Records', text: 'Progress milestones that build confidence over time.', tag: 'Student' },
    ],
  },
];



const PARTNERSHIP_TABLE = [
  { feature: 'Administrative Platform',          a: { check: true, text: 'Complete Access' },  b: { check: true, text: 'Complete Access' } },
  { feature: 'Management & Faculty Dashboards',  a: { check: true, text: 'Included' },         b: { check: true, text: 'Included' } },
  { feature: 'Student Digital Experience',       a: { tag: 'Institution Managed' },             b: { tag: 'Student Activated' } },
  { feature: 'Web & Mobile Access',              a: { check: true, text: 'Included' },         b: { check: true, text: 'Included' } },
  { feature: 'Continuous Platform Updates',      a: { check: true, text: 'Included' },         b: { check: true, text: 'Included' } },
  { feature: 'Ideal For',                        a: { text: 'Centralized Digital Strategy' },  b: { text: 'Flexible Digital Adoption' } },
];

const TESTIMONIALS_DATA = [
  { name: 'Madhavi V',   role: 'Founder, VSN Group',              quote: 'Now I can see everything happening across all our institutions in one place. It helps me make better decisions, faster.',                                              type: 'admin'    },
  { name: 'Dheeraj',     role: 'Correspondent, VSN Olympiad',     quote: 'Before, I had to follow up with different teams for every little thing. Now, everything is in one place and our day runs much smoother.',                             type: 'admin'    },
  { name: 'Rohitha',     role: 'Principal, NextGen CBSE School',  quote: "I don't have to ask five different people for updates anymore. The dashboard gives me a clear picture of the entire school in just a few minutes.",                   type: 'admin'    },
  { name: 'Muralidhar',  role: 'Teacher, VSN Smart Kids',         quote: "I don't have to carry registers or write the same information twice anymore. I update it once and everyone stays informed.",                                          type: 'employee' },
  { name: 'Ananya',      role: 'Parent',                          quote: 'My daughter sometimes forgot to tell me about homework or school events. Now I just check the app and I know everything in a minute.',                               type: 'student'  },
  { name: 'Ramesh',      role: 'Parent',                          quote: 'School messages used to get lost in WhatsApp chats. Now, I open the app and find everything in one place — fees, attendance, announcements.',                        type: 'student'  },
];

// ─── SCHOOL SCENE ────────────────────────────────────────────────────────────
const STARS = [
  { x: 4,  y: 7,  s: 1.5, delay: '0s',   dur: '3.2s' },
  { x: 11, y: 13, s: 1.0, delay: '0.5s', dur: '2.8s' },
  { x: 19, y: 5,  s: 2.0, delay: '1.1s', dur: '4.0s' },
  { x: 27, y: 18, s: 0.8, delay: '0.3s', dur: '3.5s' },
  { x: 35, y: 8,  s: 1.3, delay: '1.8s', dur: '2.5s' },
  { x: 44, y: 3,  s: 0.9, delay: '0.7s', dur: '3.8s' },
  { x: 52, y: 15, s: 1.6, delay: '2.1s', dur: '3.0s' },
  { x: 61, y: 6,  s: 1.1, delay: '0.4s', dur: '4.2s' },
  { x: 68, y: 20, s: 0.8, delay: '1.5s', dur: '2.7s' },
  { x: 74, y: 10, s: 1.4, delay: '0.9s', dur: '3.3s' },
  { x: 81, y: 4,  s: 1.0, delay: '1.3s', dur: '3.9s' },
  { x: 88, y: 14, s: 1.7, delay: '2.4s', dur: '2.6s' },
  { x: 93, y: 8,  s: 0.9, delay: '0.6s', dur: '4.5s' },
  { x: 97, y: 22, s: 1.2, delay: '1.7s', dur: '3.1s' },
  { x: 7,  y: 25, s: 0.8, delay: '2.0s', dur: '3.6s' },
  { x: 22, y: 28, s: 1.1, delay: '1.2s', dur: '2.9s' },
  { x: 48, y: 24, s: 0.9, delay: '0.8s', dur: '4.1s' },
  { x: 65, y: 27, s: 1.3, delay: '1.6s', dur: '3.4s' },
  { x: 79, y: 23, s: 1.0, delay: '2.2s', dur: '2.8s' },
  { x: 91, y: 26, s: 0.8, delay: '0.2s', dur: '3.7s' },
];

const SchoolSkyline = () => (
  <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 3, pointerEvents: 'none' }}>
    <svg viewBox="0 0 1440 280" width="100%" style={{ display: 'block', verticalAlign: 'bottom' }} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <style>{`
          @keyframes skolarBusDrive {
            0%   { transform: translateX(-180px); }
            100% { transform: translateX(1550px); }
          }
          .skolar-bus { animation: skolarBusDrive 42s linear infinite; }
        `}</style>
      </defs>

      {/* ── Ground ── */}
      <rect x="0" y="258" width="1440" height="22" fill="#090E1A"/>
      <rect x="0" y="258" width="1440" height="2" fill="#12213A"/>

      {/* ── Left residential row ── */}
      <rect x="28" y="200" width="72" height="58" fill="#16263E"/>
      <rect x="56" y="181" width="22" height="77" fill="#1A2D48"/>

      {/* ── Left trees ── */}
      <ellipse cx="183" cy="204" rx="41" ry="46" fill="#1C4028"/>
      <rect x="180" y="242" width="7" height="16" fill="#2A1A0A"/>
      <ellipse cx="248" cy="213" rx="32" ry="37" fill="#1E4A2C"/>
      <rect x="245" y="242" width="6" height="16" fill="#2A1A0A"/>
      <ellipse cx="306" cy="219" rx="26" ry="30" fill="#1A3A24"/>
      <rect x="303" y="242" width="6" height="16" fill="#2A1A0A"/>

      {/* ── Main school building (warm brick) ── */}
      <rect x="358" y="170" width="163" height="88" fill="#6B2E1A"/>
      {/* Brick texture overlay rows */}
      <rect x="358" y="185" width="163" height="2" fill="rgba(0,0,0,0.18)"/>
      <rect x="358" y="205" width="163" height="2" fill="rgba(0,0,0,0.18)"/>
      <rect x="358" y="225" width="163" height="2" fill="rgba(0,0,0,0.18)"/>
      {/* Windows row 1 — warm yellow (lit) */}
      <rect x="372" y="176" width="18" height="15" rx="2" fill="#FFD37A" fillOpacity="0.85"/>
      <rect x="400" y="176" width="18" height="15" rx="2" fill="#FFD37A" fillOpacity="0.90"/>
      <rect x="428" y="176" width="18" height="15" rx="2" fill="#FFD37A" fillOpacity="0.78"/>
      <rect x="456" y="176" width="18" height="15" rx="2" fill="#FFD37A" fillOpacity="0.88"/>
      {/* Windows row 2 */}
      <rect x="372" y="206" width="18" height="15" rx="2" fill="#FFD37A" fillOpacity="0.55"/>
      <rect x="400" y="206" width="18" height="15" rx="2" fill="#A8D8F0" fillOpacity="0.60"/>
      <rect x="428" y="206" width="18" height="15" rx="2" fill="#FFD37A" fillOpacity="0.42"/>
      <rect x="456" y="206" width="18" height="15" rx="2" fill="#FFD37A" fillOpacity="0.65"/>
      {/* Entrance door */}
      <rect x="418" y="233" width="22" height="25" rx="2" fill="#0A0E18"/>

      {/* ── Large central campus (deep navy-indigo) ── */}
      <rect x="503" y="110" width="312" height="148" fill="#192038"/>
      {/* Window rows — campus */}
      <rect x="518" y="126" width="20" height="18" rx="2" fill="#FFD37A" fillOpacity="0.80"/>
      <rect x="551" y="126" width="20" height="18" rx="2" fill="#FFD37A" fillOpacity="0.68"/>
      <rect x="584" y="126" width="20" height="18" rx="2" fill="#A8D8F0" fillOpacity="0.62"/>
      <rect x="698" y="126" width="20" height="18" rx="2" fill="#FFD37A" fillOpacity="0.84"/>
      <rect x="731" y="126" width="20" height="18" rx="2" fill="#FFD37A" fillOpacity="0.58"/>
      <rect x="764" y="126" width="20" height="18" rx="2" fill="#A8D8F0" fillOpacity="0.68"/>
      <rect x="518" y="157" width="20" height="18" rx="2" fill="#FFD37A" fillOpacity="0.60"/>
      <rect x="551" y="157" width="20" height="18" rx="2" fill="#FFD37A" fillOpacity="0.84"/>
      <rect x="584" y="157" width="20" height="18" rx="2" fill="#A8D8F0" fillOpacity="0.50"/>
      <rect x="698" y="157" width="20" height="18" rx="2" fill="#FFD37A" fillOpacity="0.78"/>
      <rect x="731" y="157" width="20" height="18" rx="2" fill="#FFD37A" fillOpacity="0.66"/>
      <rect x="764" y="157" width="20" height="18" rx="2" fill="#A8D8F0" fillOpacity="0.58"/>
      <rect x="518" y="188" width="20" height="14" rx="2" fill="#FFD37A" fillOpacity="0.38"/>
      <rect x="584" y="188" width="20" height="14" rx="2" fill="#FFD37A" fillOpacity="0.42"/>
      <rect x="698" y="188" width="20" height="14" rx="2" fill="#A8D8F0" fillOpacity="0.40"/>
      <rect x="764" y="188" width="20" height="14" rx="2" fill="#FFD37A" fillOpacity="0.36"/>

      {/* ── Central lower connector block ── */}
      <rect x="614" y="168" width="88" height="90" fill="#1E2A4A"/>
      {/* Entrance arch */}
      <rect x="646" y="213" width="24" height="45" rx="3" fill="#080D1A"/>

      {/* ── Clock tower — centerpiece (sandstone/stone) ── */}
      <rect x="647" y="29" width="62" height="229" fill="#28203A"/>
      <polygon points="647,29 709,29 678,4" fill="#342845"/>
      {/* Accent vertical lines on tower */}
      <rect x="659" y="50" width="3" height="180" fill="rgba(167,139,250,0.08)"/>
      <rect x="697" y="50" width="3" height="180" fill="rgba(167,139,250,0.08)"/>
      {/* Tower window large */}
      <rect x="659" y="105" width="20" height="36" rx="4" fill="#FFD37A" fillOpacity="0.85"/>
      <rect x="659" y="155" width="20" height="23" rx="2" fill="#FFD37A" fillOpacity="0.70"/>
      {/* Clock face */}
      <circle cx="678" cy="66" r="26" fill="rgba(255,211,122,0.06)" stroke="#C4B5FD" strokeWidth="2" opacity="0.80"/>
      <circle cx="678" cy="66" r="3" fill="#C4B5FD" opacity="0.85"/>
      <line x1="678" y1="66" x2="678" y2="47" stroke="#C4B5FD" strokeWidth="2.5" opacity="0.85"/>
      <line x1="678" y1="66" x2="694" y2="66" stroke="#C4B5FD" strokeWidth="2" opacity="0.75"/>
      {/* Clock hour markers */}
      <line x1="678" y1="42" x2="678" y2="46" stroke="#C4B5FD" strokeWidth="1.5" opacity="0.55"/>
      <line x1="678" y1="86" x2="678" y2="90" stroke="#C4B5FD" strokeWidth="1.5" opacity="0.55"/>
      <line x1="654" y1="66" x2="658" y2="66" stroke="#C4B5FD" strokeWidth="1.5" opacity="0.55"/>
      <line x1="698" y1="66" x2="702" y2="66" stroke="#C4B5FD" strokeWidth="1.5" opacity="0.55"/>
      {/* Flag */}
      <line x1="678" y1="4" x2="678" y2="-10" stroke="#C4B5FD" strokeWidth="1.5" opacity="0.60"/>
      <polygon points="678,-10 702,-4 678,4" fill="#A78BFA" opacity="0.70"/>

      {/* ── Right admin buildings (steel blue) ── */}
      <rect x="813" y="152" width="182" height="106" fill="#1C2E48"/>
      <rect x="826" y="168" width="18" height="15" rx="2" fill="#FFD37A" fillOpacity="0.80"/>
      <rect x="854" y="168" width="18" height="15" rx="2" fill="#A8D8F0" fillOpacity="0.65"/>
      <rect x="882" y="168" width="18" height="15" rx="2" fill="#FFD37A" fillOpacity="0.80"/>
      <rect x="910" y="168" width="18" height="15" rx="2" fill="#FFD37A" fillOpacity="0.55"/>
      <rect x="826" y="197" width="18" height="15" rx="2" fill="#A8D8F0" fillOpacity="0.50"/>
      <rect x="854" y="197" width="18" height="15" rx="2" fill="#FFD37A" fillOpacity="0.42"/>
      <rect x="882" y="197" width="18" height="15" rx="2" fill="#FFD37A" fillOpacity="0.70"/>
      <rect x="910" y="197" width="18" height="15" rx="2" fill="#A8D8F0" fillOpacity="0.48"/>

      {/* ── Far-right small building ── */}
      <rect x="980" y="191" width="113" height="67" fill="#152030"/>
      <rect x="992" y="202" width="15" height="13" rx="1" fill="#FFD37A" fillOpacity="0.68"/>
      <rect x="1016" y="202" width="15" height="13" rx="1" fill="#FFD37A" fillOpacity="0.50"/>
      <rect x="1040" y="202" width="15" height="13" rx="1" fill="#A8D8F0" fillOpacity="0.52"/>
      <rect x="1064" y="202" width="15" height="13" rx="1" fill="#FFD37A" fillOpacity="0.60"/>

      {/* ── Right trees ── */}
      <ellipse cx="1150" cy="210" rx="36" ry="41" fill="#1C4028"/>
      <rect x="1147" y="242" width="7" height="16" fill="#2A1A0A"/>
      <ellipse cx="1210" cy="217" rx="30" ry="34" fill="#1E4A2C"/>
      <rect x="1207" y="242" width="6" height="16" fill="#2A1A0A"/>
      <ellipse cx="1266" cy="222" rx="23" ry="27" fill="#1A3A24"/>
      <rect x="1263" y="242" width="5" height="16" fill="#2A1A0A"/>

      {/* ── Far right partial building ── */}
      <rect x="1340" y="204" width="100" height="54" fill="#16263E"/>
      <rect x="1353" y="214" width="15" height="13" rx="1" fill="#FFD37A" fillOpacity="0.60"/>
      <rect x="1378" y="214" width="15" height="13" rx="1" fill="#FFD37A" fillOpacity="0.45"/>


      {/* ── Street lamps ── */}
      <line x1="438" y1="258" x2="438" y2="204" stroke="#1C2A3E" strokeWidth="2.5"/>
      <line x1="433" y1="204" x2="443" y2="204" stroke="#1C2A3E" strokeWidth="2"/>
      <circle cx="438" cy="202" r="7" fill="#FFE87A" fillOpacity="0.50"/>
      <circle cx="438" cy="202" r="14" fill="#FFE87A" fillOpacity="0.08"/>
      <line x1="1028" y1="258" x2="1028" y2="204" stroke="#1C2A3E" strokeWidth="2.5"/>
      <line x1="1023" y1="204" x2="1033" y2="204" stroke="#1C2A3E" strokeWidth="2"/>
      <circle cx="1028" cy="202" r="7" fill="#FFE87A" fillOpacity="0.50"/>
      <circle cx="1028" cy="202" r="14" fill="#FFE87A" fillOpacity="0.08"/>

      {/* ── School bus (yellow) ── */}
      <g className="skolar-bus">
        {/* Body */}
        <rect x="5"   y="217" width="118" height="36" rx="5" fill="#FFD700"/>
        {/* Front cab */}
        <rect x="100" y="217" width="23"  height="36" rx="4" fill="#E8A000"/>
        {/* Black roof stripe */}
        <rect x="5"   y="217" width="118" height="5"  rx="3" fill="#CC8C00"/>
        {/* Windows */}
        <rect x="12"  y="224" width="18"  height="14" rx="2" fill="#87CEEB" fillOpacity="0.80"/>
        <rect x="36"  y="224" width="18"  height="14" rx="2" fill="#87CEEB" fillOpacity="0.80"/>
        <rect x="60"  y="224" width="18"  height="14" rx="2" fill="#87CEEB" fillOpacity="0.75"/>
        <rect x="84"  y="224" width="12"  height="14" rx="2" fill="#87CEEB" fillOpacity="0.70"/>
        {/* Front windshield */}
        <rect x="104" y="222" width="14"  height="16" rx="2" fill="#A8D8F0" fillOpacity="0.70"/>
        {/* Headlight */}
        <circle cx="121" cy="238" r="4" fill="#FFE87A" fillOpacity="0.95"/>
        {/* Red warning light on top */}
        <rect x="48" y="213" width="8" height="4" rx="1" fill="#FF3020" fillOpacity="0.85"/>
        {/* Wheels */}
        <circle cx="27"  cy="254" r="10" fill="#1A1A28"/>
        <circle cx="27"  cy="254" r="5"  fill="#2C2C3C"/>
        <circle cx="27"  cy="254" r="2"  fill="#3A3A4C"/>
        <circle cx="96"  cy="254" r="10" fill="#1A1A28"/>
        <circle cx="96"  cy="254" r="5"  fill="#2C2C3C"/>
        <circle cx="96"  cy="254" r="2"  fill="#3A3A4C"/>
        {/* Wheel wells */}
        <rect x="17" y="245" width="20" height="8" rx="3" fill="#CC8C00"/>
        <rect x="86" y="245" width="20" height="8" rx="3" fill="#CC8C00"/>
      </g>
    </svg>
  </Box>
);

// ─── MOTION HELPERS ──────────────────────────────────────────────────────────
const MotionBox = motion.create(Box);

const Reveal = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{ duration: 0.6, ease: 'easeOut', delay }}
  >
    {children}
  </motion.div>
);

// ─── SHARED MODAL INPUT STYLE ─────────────────────────────────────────────────
const mInputSx = {
  width: '100%', display: 'block', bgcolor: '#F8FAFC',
  border: '1px solid #E2E8F0', borderRadius: '10px',
  color: '#0F172A', fontSize: '0.875rem', fontFamily: '"DM Sans", sans-serif',
  outline: 'none', p: '10px 14px',
  '&:focus': { borderColor: '#7C63D8', boxShadow: '0 0 0 2px rgba(124,99,216,0.15)' },
  '&::placeholder': { color: '#94A3B8' },
} as const;

// ─── ADMIN DASHBOARD MOCKUP ──────────────────────────────────────────────────
function AdminDashboardContent() {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [studentCount, setStudentCount] = useState(0);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [feesCount, setFeesCount] = useState(0);

  useEffect(() => {
    const el = triggerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setReady(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!ready) return;
    let frame = 0;
    const id = setInterval(() => {
      frame++;
      const p = 1 - Math.pow(1 - frame / 50, 3);
      setStudentCount(Math.round(1000 * p));
      setEmployeeCount(Math.round(46 * p));
      setFeesCount(Math.round(19500 * p));
      if (frame >= 50) {
        setStudentCount(1000); setEmployeeCount(46); setFeesCount(19500);
        clearInterval(id);
      }
    }, 20);
    return () => clearInterval(id);
  }, [ready]);

  const r = 22;
  const circ = 2 * Math.PI * r;
  const arcLen = circ * 0.12;

  const SIDEBAR_ITEMS = [
    { label: 'Dashboard', active: true, arrow: false },
    { label: 'Administration', active: false, arrow: true },
    { label: 'Students', active: false, arrow: true },
    { label: 'Employees', active: false, arrow: true },
    { label: 'Attendance', active: false, arrow: true },
    { label: 'Exams', active: false, arrow: true },
    { label: 'Syllabus', active: false, arrow: false },
    { label: 'Assignments', active: false, arrow: false },
    { label: 'Timetable', active: false, arrow: true },
    { label: 'Leaves & Holidays', active: false, arrow: true },
    { label: 'Transport', active: false, arrow: true },
  ];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', minHeight: '380px' }}>

      {/* Sidebar */}
      <Box sx={{ bgcolor: '#1C1C2E', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '1px solid rgba(255,255,255,0.04)' }}>
        <Box sx={{ px: '10px', py: '11px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          <Box sx={{ width: 22, height: 22, bgcolor: '#FF6B35', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', flexShrink: 0 }}>🎓</Box>
          <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '10.5px', fontWeight: 700, color: '#fff', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Vidyanikethan</Typography>
        </Box>
        <Box sx={{ px: '6px', py: '8px', overflow: 'hidden', flex: 1 }}>
          {SIDEBAR_ITEMS.map(item => (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: '5px', px: '8px', py: '5px', borderRadius: '6px', mb: '1px', bgcolor: item.active ? '#FF6B35' : 'transparent' }}>
              <Typography sx={{ fontSize: '9.5px', color: item.active ? '#fff' : 'rgba(255,255,255,0.55)', fontWeight: item.active ? 600 : 400, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</Typography>
              {item.arrow && !item.active && <Typography sx={{ fontSize: '7px', color: 'rgba(255,255,255,0.28)', flexShrink: 0 }}>›</Typography>}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Main content */}
      <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#F8FAFC' }}>

        {/* Top bar */}
        <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #E5E7EB', px: '12px', py: '7px', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', bgcolor: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: '5px', px: '8px', py: '3px', flex: 1, maxWidth: '150px' }}>
            <Typography sx={{ fontSize: '8.5px', color: '#94A3B8' }}>🔍</Typography>
            <Typography sx={{ fontSize: '9px', color: '#CBD5E1' }}>Search pages...</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid #E5E7EB', borderRadius: '5px', px: '7px', py: '3px', flexShrink: 0 }}>
            <Typography sx={{ fontSize: '8.5px', color: '#64748B' }}>2025-2026 ∨</Typography>
          </Box>
          <Typography sx={{ fontSize: '13px', color: '#94A3B8', flexShrink: 0 }}>🔔</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
            <Box sx={{ width: 22, height: 22, bgcolor: '#7C63D8', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: '8px', fontWeight: 800, color: '#fff' }}>ST</Typography>
            </Box>
            <Typography sx={{ fontSize: '9px', color: '#374151', fontWeight: 500 }}>Swetha T</Typography>
          </Box>
        </Box>

        {/* Hero banner */}
        <Box sx={{ mx: '10px', mt: '8px', px: '14px', py: '10px', bgcolor: '#1C1C2E', borderRadius: '10px', position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
          {[
            { top: '20%', right: '40%', size: 5, color: '#00D8FF', delay: '0s' },
            { top: '65%', right: '46%', size: 4, color: '#FFD700', delay: '0.7s' },
            { top: '30%', right: '55%', size: 3, color: '#FF69B4', delay: '1.3s' },
            { top: '75%', right: '33%', size: 4, color: '#00FF99', delay: '1.9s' },
            { top: '45%', right: '20%', size: 3, color: '#FF6B35', delay: '1.1s' },
          ].map((dot, di) => (
            <Box key={di} sx={{ position: 'absolute', top: dot.top, right: dot.right, width: dot.size, height: dot.size, borderRadius: '50%', bgcolor: dot.color, '@keyframes floatDot': { '0%,100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(-3px)' } }, animation: `floatDot 2.5s ease-in-out ${dot.delay} infinite` }} />
          ))}
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography sx={{ fontSize: '7px', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.12em', textTransform: 'uppercase', mb: '3px' }}>@ Institution</Typography>
            <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '15px', fontWeight: 800, color: '#fff', lineHeight: 1.1, mb: '4px' }}>Vidyanikethan</Typography>
            <Typography sx={{ fontSize: '9px', color: 'rgba(255,255,255,0.65)', mb: '6px' }}>Good evening, <Box component="strong" sx={{ color: '#fff', fontWeight: 700 }}>Swetha T!</Box></Typography>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '8px', py: '2px', bgcolor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '100px' }}>
              <Typography sx={{ fontSize: '7.5px', color: 'rgba(255,255,255,0.55)' }}>InstituteAdmin</Typography>
            </Box>
          </Box>
        </Box>

        {/* 4 stat cards */}
        <Box ref={triggerRef} sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', px: '10px', mt: '8px', flexShrink: 0 }}>
          {[
            { display: studentCount.toLocaleString(), label: 'Total Students', delta: '+100%', pos: true },
            { display: String(employeeCount), label: 'Total Employees', delta: '+100%', pos: true },
            { display: '0.19%', label: "Today's Attendance", delta: '+100%', pos: true },
            { display: '₹' + feesCount.toLocaleString(), label: 'Fees This Month', delta: '-2%', pos: false },
          ].map((s, si) => (
            <Box key={si} sx={{ bgcolor: '#fff', border: '1.5px solid rgba(99,91,255,0.22)', borderRadius: '8px', p: '7px 8px' }}>
              <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '11px', fontWeight: 800, color: '#111827', lineHeight: 1, mb: '3px' }}>{s.display}</Typography>
              <Typography sx={{ fontSize: '7.5px', color: '#64748B', mb: '4px', lineHeight: 1.3 }}>{s.label}</Typography>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '4px', py: '1.5px', bgcolor: s.pos ? '#dcfce7' : '#fee2e2', borderRadius: '100px' }}>
                <Typography sx={{ fontSize: '7px', fontWeight: 700, color: s.pos ? '#16a34a' : '#dc2626' }}>{s.delta}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Charts */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: '6px', px: '10px', mt: '6px', pb: '10px' }}>

          {/* Attendance trend */}
          <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', p: '8px 10px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '6px' }}>
              <Typography sx={{ fontSize: '9.5px', fontWeight: 700, color: '#111827' }}>Attendance Trend</Typography>
              <Box sx={{ display: 'flex', gap: '8px' }}>
                {[{ label: 'Students', color: '#4F46E5' }, { label: 'Staff', color: '#10B981' }].map(l => (
                  <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <Box sx={{ width: 8, height: 2, borderRadius: '1px', bgcolor: l.color }} />
                    <Typography sx={{ fontSize: '7px', color: '#64748B' }}>{l.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <svg width="100%" height="58" viewBox="0 0 210 58" preserveAspectRatio="none">
              {[0, 14.5, 29, 43.5, 58].map(y => (
                <line key={y} x1="0" y1={y} x2="210" y2={y} stroke="#F1F5F9" strokeWidth="0.8"/>
              ))}
              <polyline
                points="0,50 21,50 42,50 63,50 84,50 105,48 126,50 147,50 168,48 189,48 210,45"
                fill="none" stroke="#4F46E5" strokeWidth="1.5" strokeLinejoin="round"
                strokeDasharray="500"
                style={{ strokeDashoffset: ready ? 0 : 500, transition: 'stroke-dashoffset 1s ease-out 0.2s' } as React.CSSProperties}
              />
              <polyline
                points="0,40 21,42 42,44 63,40 84,38 105,36 126,40 147,38 168,35 189,32 210,30"
                fill="none" stroke="#10B981" strokeWidth="1.5" strokeLinejoin="round"
                strokeDasharray="500"
                style={{ strokeDashoffset: ready ? 0 : 500, transition: 'stroke-dashoffset 1s ease-out 0.4s' } as React.CSSProperties}
              />
            </svg>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '2px' }}>
              {['Jun 1', 'Jun 3', 'Jun 5', 'Jun 7', 'Jun 9', 'Jun 11'].map(d => (
                <Typography key={d} sx={{ fontSize: '6px', color: '#94A3B8' }}>{d}</Typography>
              ))}
            </Box>
          </Box>

          {/* Fee collection donut */}
          <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', p: '8px 10px' }}>
            <Typography sx={{ fontSize: '9.5px', fontWeight: 700, color: '#111827', mb: '8px' }}>Fee Collection</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Box sx={{ position: 'relative', flexShrink: 0, width: 60, height: 60 }}>
                <svg width="60" height="60" viewBox="0 0 60 60" style={{ transform: 'rotate(-90deg)' }}>
                  <circle cx="30" cy="30" r={r} fill="none" stroke="#FCA5A5" strokeWidth="9"/>
                  <circle
                    cx="30" cy="30" r={r} fill="none" stroke="#7C63D8" strokeWidth="9"
                    strokeDasharray={`${arcLen} ${circ}`}
                    strokeLinecap="round"
                    style={{ strokeDashoffset: ready ? 0 : circ, transition: 'stroke-dashoffset 1.3s ease-out 0.4s' } as React.CSSProperties}
                  />
                </svg>
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1px' }}>
                  <Typography sx={{ fontSize: '5.5px', color: '#94A3B8', lineHeight: 1 }}>Total</Typography>
                  <Typography sx={{ fontSize: '6.5px', fontWeight: 800, color: '#7C63D8', lineHeight: 1 }}>₹31.6M</Typography>
                </Box>
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '2px' }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#7C63D8', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '8px', color: '#475569' }}>Collected</Typography>
                </Box>
                <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#7C63D8', mb: '7px', pl: '11px' }}>₹39,500</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '2px' }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#FCA5A5', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '8px', color: '#475569' }}>Pending</Typography>
                </Box>
                <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#EF4444', pl: '11px' }}>₹31,643,400</Typography>
              </Box>
            </Box>
          </Box>

        </Box>
      </Box>
    </Box>
  );
}

// ─── SHARED HERO BANNER (dots only, no character) ────────────────────────────
const HERO_DOTS = [
  { top: '22%', right: '18%', size: 5, color: '#00D8FF', delay: '0s' },
  { top: '68%', right: '30%', size: 4, color: '#FFD700', delay: '0.7s' },
  { top: '35%', right: '44%', size: 3, color: '#FF69B4', delay: '1.3s' },
  { top: '78%', right: '12%', size: 4, color: '#00FF99', delay: '1.9s' },
  { top: '50%', right: '55%', size: 3, color: '#A78BFA', delay: '2.4s' },
];

function DashHeroBanner({ name, idCode, badge }: { name: string; idCode: string; badge: string }) {
  return (
    <Box sx={{ mx: '10px', mt: '8px', px: '14px', py: '10px', bgcolor: '#1C1C2E', borderRadius: '10px', position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
      {HERO_DOTS.map((dot, di) => (
        <Box key={di} sx={{ position: 'absolute', top: dot.top, right: dot.right, width: dot.size, height: dot.size, borderRadius: '50%', bgcolor: dot.color, '@keyframes floatDot': { '0%,100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(-3px)' } }, animation: `floatDot 2.5s ease-in-out ${dot.delay} infinite` }} />
      ))}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Typography sx={{ fontSize: '7px', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.12em', textTransform: 'uppercase', mb: '3px' }}>@ Institution</Typography>
        <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '15px', fontWeight: 800, color: '#fff', lineHeight: 1.1, mb: '4px' }}>Vidyanikethan</Typography>
        <Typography sx={{ fontSize: '9px', color: 'rgba(255,255,255,0.65)', mb: '5px' }}>Good evening, <Box component="strong" sx={{ color: '#fff', fontWeight: 700 }}>{name}!</Box></Typography>
        <Box sx={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '7px', py: '2px', bgcolor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '100px' }}>
            <Typography sx={{ fontSize: '7px', color: 'rgba(255,255,255,0.55)' }}>ID: {idCode}</Typography>
          </Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '7px', py: '2px', bgcolor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '100px' }}>
            <Typography sx={{ fontSize: '7px', color: 'rgba(255,255,255,0.55)' }}>{badge}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ─── SHARED DASH TOPBAR ───────────────────────────────────────────────────────
function DashTopBar({ initials, userName, showNotifBadge = false }: { initials: string; userName: string; showNotifBadge?: boolean }) {
  return (
    <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #E5E7EB', px: '12px', py: '7px', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', bgcolor: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: '5px', px: '8px', py: '3px', flex: 1, maxWidth: '150px' }}>
        <Typography sx={{ fontSize: '8.5px', color: '#94A3B8' }}>🔍</Typography>
        <Typography sx={{ fontSize: '9px', color: '#CBD5E1' }}>Search pages...</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid #E5E7EB', borderRadius: '5px', px: '7px', py: '3px', flexShrink: 0 }}>
        <Typography sx={{ fontSize: '8.5px', color: '#64748B' }}>2025-2026 ∨</Typography>
      </Box>
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <Typography sx={{ fontSize: '13px', color: '#94A3B8' }}>🔔</Typography>
        {showNotifBadge && (
          <Box sx={{ position: 'absolute', top: -2, right: -4, width: 10, height: 10, bgcolor: '#EF4444', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '6px', fontWeight: 800, color: '#fff', lineHeight: 1 }}>3</Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
        <Box sx={{ width: 22, height: 22, bgcolor: '#7C63D8', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: '8px', fontWeight: 800, color: '#fff' }}>{initials}</Typography>
        </Box>
        <Typography sx={{ fontSize: '9px', color: '#374151', fontWeight: 500 }}>{userName}</Typography>
      </Box>
    </Box>
  );
}

// ─── SHARED DASH SIDEBAR ──────────────────────────────────────────────────────
function DashSidebar({ items }: { items: { label: string; active?: boolean; arrow?: boolean }[] }) {
  return (
    <Box sx={{ bgcolor: '#1C1C2E', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '1px solid rgba(255,255,255,0.04)' }}>
      <Box sx={{ px: '10px', py: '11px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
        <Box sx={{ width: 22, height: 22, bgcolor: '#FF6B35', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', flexShrink: 0 }}>🎓</Box>
        <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '10.5px', fontWeight: 700, color: '#fff', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Vidyanikethan</Typography>
      </Box>
      <Box sx={{ px: '6px', py: '8px', overflow: 'hidden', flex: 1 }}>
        {items.map(item => (
          <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: '5px', px: '8px', py: '5px', borderRadius: '6px', mb: '1px', bgcolor: item.active ? '#FF6B35' : 'transparent' }}>
            <Typography sx={{ fontSize: '9.5px', color: item.active ? '#fff' : 'rgba(255,255,255,0.55)', fontWeight: item.active ? 600 : 400, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</Typography>
            {item.arrow && !item.active && <Typography sx={{ fontSize: '7px', color: 'rgba(255,255,255,0.28)', flexShrink: 0 }}>›</Typography>}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ─── TEACHER DASHBOARD MOCKUP ─────────────────────────────────────────────────
function TeacherDashboardContent() {
  const TEACHER_SIDEBAR = [
    { label: 'Dashboard', active: true },
    { label: 'Students', arrow: true },
    { label: 'Attendance', arrow: true },
    { label: 'My Exams' },
    { label: 'Syllabus' },
    { label: 'Assignments' },
    { label: 'Homework' },
    { label: 'Online Classes', arrow: true },
    { label: 'Timetable', arrow: true },
    { label: 'Leaves & Holidays', arrow: true },
    { label: 'Communication', arrow: true },
  ];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', minHeight: '380px' }}>
      <DashSidebar items={TEACHER_SIDEBAR} />
      <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#F8FAFC' }}>
        <DashTopBar initials="RM" userName="Rahul Mehta" showNotifBadge />
        <DashHeroBanner name="Rahul Mehta" idCode="VIDE260038" badge="Teacher" />

        {/* 3-card row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '7px', px: '10px', mt: '8px', flexShrink: 0 }}>

          {/* Time Tracking */}
          <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '10px', p: '10px 11px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '7px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Box sx={{ width: 16, height: 16, bgcolor: '#EFF6FF', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px' }}>⏰</Box>
                <Typography sx={{ fontSize: '8.5px', fontWeight: 600, color: '#374151' }}>Time Tracking</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '13px', fontWeight: 800, color: '#111827', lineHeight: 1 }}>10:37</Typography>
                <Typography sx={{ fontSize: '6.5px', color: '#94A3B8' }}>pm</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px', mb: '7px' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: '14px', color: '#10B981', lineHeight: 1 }}>→</Typography>
                <Typography sx={{ fontSize: '7px', color: '#64748B' }}>Check-in</Typography>
                <Typography sx={{ fontSize: '8px', fontWeight: 600, color: '#374151' }}>—</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: '14px', color: '#EF4444', lineHeight: 1 }}>←</Typography>
                <Typography sx={{ fontSize: '7px', color: '#64748B' }}>Check-Out</Typography>
                <Typography sx={{ fontSize: '8px', fontWeight: 600, color: '#374151' }}>—</Typography>
              </Box>
            </Box>
            <Box sx={{ bgcolor: '#7C63D8', borderRadius: '5px', py: '5px', textAlign: 'center', cursor: 'default' }}>
              <Typography sx={{ fontSize: '8px', fontWeight: 700, color: '#fff' }}>Check In</Typography>
            </Box>
          </Box>

          {/* Leave Summary */}
          <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '10px', p: '10px 11px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '7px' }}>
              <Box sx={{ width: 16, height: 16, bgcolor: '#F3F0FF', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px' }}>📅</Box>
              <Typography sx={{ fontSize: '8.5px', fontWeight: 600, color: '#374151' }}>Leave Summary</Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px', mb: '6px' }}>
              <Box sx={{ bgcolor: '#EFF6FF', borderRadius: '5px', p: '4px', textAlign: 'center' }}>
                <Typography sx={{ fontSize: '6.5px', color: '#3B82F6', fontWeight: 600 }}>Casual</Typography>
                <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '12px', fontWeight: 800, color: '#3B82F6', lineHeight: 1.1 }}>6</Typography>
              </Box>
              <Box sx={{ bgcolor: '#F0FDF4', borderRadius: '5px', p: '4px', textAlign: 'center' }}>
                <Typography sx={{ fontSize: '6.5px', color: '#10B981', fontWeight: 600 }}>Sick</Typography>
                <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '12px', fontWeight: 800, color: '#10B981', lineHeight: 1.1 }}>6</Typography>
              </Box>
              <Box sx={{ bgcolor: '#F8FAFC', borderRadius: '5px', p: '4px', textAlign: 'center' }}>
                <Typography sx={{ fontSize: '6.5px', color: '#64748B', fontWeight: 600 }}>Optional</Typography>
                <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '12px', fontWeight: 800, color: '#374151', lineHeight: 1.1 }}>2</Typography>
              </Box>
            </Box>
            <Typography sx={{ fontSize: '7.5px', fontWeight: 600, color: '#374151', mb: '2px' }}>Recent Requests</Typography>
            <Typography sx={{ fontSize: '7px', color: '#94A3B8', mb: '6px' }}>No recent requests found.</Typography>
            <Box sx={{ bgcolor: '#7C63D8', borderRadius: '5px', py: '5px', textAlign: 'center', cursor: 'default' }}>
              <Typography sx={{ fontSize: '8px', fontWeight: 700, color: '#fff' }}>Apply for Leave</Typography>
            </Box>
          </Box>

          {/* Announcements */}
          <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '10px', p: '10px 11px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '7px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Box sx={{ width: 16, height: 16, bgcolor: '#FFF7ED', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px' }}>📢</Box>
                <Typography sx={{ fontSize: '8.5px', fontWeight: 600, color: '#374151' }}>Announcements</Typography>
              </Box>
              <Typography sx={{ fontSize: '7px', color: '#7C63D8', fontWeight: 600 }}>View All ›</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '58px' }}>
              <Typography sx={{ fontSize: '7.5px', color: '#94A3B8', textAlign: 'center', lineHeight: 1.6 }}>No announcements<br/>available at the moment.</Typography>
            </Box>
          </Box>

        </Box>

        {/* My Schedule breadcrumb */}
        <Box sx={{ px: '10px', mt: '8px', pb: '10px' }}>
          <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center', mb: '3px' }}>
            <Typography sx={{ fontSize: '7px', color: '#94A3B8' }}>Home</Typography>
            <Typography sx={{ fontSize: '7px', color: '#94A3B8' }}>›</Typography>
            <Typography sx={{ fontSize: '7px', color: '#64748B' }}>My Schedule</Typography>
          </Box>
          <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '11px', fontWeight: 800, color: '#7C63D8', mb: '1px' }}>My Schedule</Typography>
          <Typography sx={{ fontSize: '7.5px', color: '#94A3B8' }}>Your teaching schedule and class assignments</Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ─── MANAGEMENT DASHBOARD MOCKUP ──────────────────────────────────────────────
function ManagementDashboardContent() {
  const MGMT_SIDEBAR = [
    { label: 'Dashboard', active: true },
    { label: 'Overview', arrow: true },
    { label: 'Analytics', arrow: true },
    { label: 'Reports', arrow: true },
    { label: 'Governance' },
    { label: 'Campuses', arrow: true },
    { label: 'Finance', arrow: true },
    { label: 'Compliance' },
    { label: 'Leaves & Holidays', arrow: true },
    { label: 'Communication', arrow: true },
  ];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', minHeight: '380px' }}>
      <DashSidebar items={MGMT_SIDEBAR} />
      <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#F8FAFC' }}>
        <DashTopBar initials="VN" userName="Vikram Nair" />
        <DashHeroBanner name="Vikram Nair" idCode="VMGR100001" badge="Management" />

        {/* 3-card row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '7px', px: '10px', mt: '8px', flexShrink: 0 }}>

          {/* Campus Overview */}
          <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '10px', p: '10px 11px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '7px' }}>
              <Box sx={{ width: 16, height: 16, bgcolor: '#F3F0FF', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px' }}>🏫</Box>
              <Typography sx={{ fontSize: '8.5px', fontWeight: 600, color: '#374151' }}>Campus Overview</Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px', mb: '7px' }}>
              <Box sx={{ bgcolor: '#F3F0FF', borderRadius: '5px', p: '6px', textAlign: 'center' }}>
                <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '14px', fontWeight: 800, color: '#7C63D8', lineHeight: 1 }}>3</Typography>
                <Typography sx={{ fontSize: '6.5px', color: '#7C63D8', mt: '2px' }}>Active Campuses</Typography>
              </Box>
              <Box sx={{ bgcolor: '#EFF6FF', borderRadius: '5px', p: '6px', textAlign: 'center' }}>
                <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '14px', fontWeight: 800, color: '#3B82F6', lineHeight: 1 }}>4.8K</Typography>
                <Typography sx={{ fontSize: '6.5px', color: '#3B82F6', mt: '2px' }}>Total Students</Typography>
              </Box>
            </Box>
            {/* Mini performance bar */}
            <Typography sx={{ fontSize: '7px', color: '#94A3B8', mb: '3px' }}>Avg. Campus Performance</Typography>
            <Box sx={{ bgcolor: '#E5E7EB', borderRadius: '3px', height: '5px', mb: '2px' }}>
              <Box sx={{ bgcolor: '#7C63D8', borderRadius: '3px', height: '5px', width: '92%' }} />
            </Box>
            <Typography sx={{ fontSize: '7px', color: '#7C63D8', fontWeight: 700 }}>92%</Typography>
          </Box>

          {/* Governance */}
          <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '10px', p: '10px 11px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '7px' }}>
              <Box sx={{ width: 16, height: 16, bgcolor: '#FFF7ED', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px' }}>📋</Box>
              <Typography sx={{ fontSize: '8.5px', fontWeight: 600, color: '#374151' }}>Governance</Typography>
            </Box>
            {[
              { label: 'Q2 Report Generated', badge: 'Ready', color: '#10B981', bg: '#d1fae5' },
              { label: 'New Campus Onboarding', badge: 'Active', color: '#10B981', bg: '#d1fae5' },
              { label: 'Compliance Audit Due', badge: 'Soon', color: '#d97706', bg: '#fef3c7' },
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: '5px', borderBottom: i < 2 ? '1px dashed #E5E7EB' : 'none' }}>
                <Typography sx={{ fontSize: '8px', color: '#374151', flex: 1, pr: '4px', lineHeight: 1.3 }}>{item.label}</Typography>
                <Box sx={{ px: '5px', py: '2px', borderRadius: '4px', bgcolor: item.bg }}>
                  <Typography sx={{ fontSize: '7px', fontWeight: 700, color: item.color }}>{item.badge}</Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Finance Snapshot */}
          <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '10px', p: '10px 11px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '7px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Box sx={{ width: 16, height: 16, bgcolor: '#F0FDF4', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px' }}>📈</Box>
                <Typography sx={{ fontSize: '8.5px', fontWeight: 600, color: '#374151' }}>Finance</Typography>
              </Box>
              <Typography sx={{ fontSize: '7px', color: '#7C63D8', fontWeight: 600 }}>View All ›</Typography>
            </Box>
            {[
              { label: 'Fee Collected', value: '₹2.4Cr', color: '#059669' },
              { label: 'Pending Fees', value: '₹18.2L', color: '#EF4444' },
              { label: 'Staff Payroll', value: '₹46.8L', color: '#374151' },
            ].map((row, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: '4px', borderBottom: i < 2 ? '1px solid #F1F5F9' : 'none' }}>
                <Typography sx={{ fontSize: '7.5px', color: '#64748B' }}>{row.label}</Typography>
                <Typography sx={{ fontSize: '8.5px', fontWeight: 700, color: row.color }}>{row.value}</Typography>
              </Box>
            ))}
          </Box>

        </Box>

        {/* Key Metrics breadcrumb */}
        <Box sx={{ px: '10px', mt: '8px', pb: '10px' }}>
          <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center', mb: '3px' }}>
            <Typography sx={{ fontSize: '7px', color: '#94A3B8' }}>Home</Typography>
            <Typography sx={{ fontSize: '7px', color: '#94A3B8' }}>›</Typography>
            <Typography sx={{ fontSize: '7px', color: '#64748B' }}>Key Metrics</Typography>
          </Box>
          <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '11px', fontWeight: 800, color: '#7C63D8', mb: '1px' }}>Institution Overview</Typography>
          <Typography sx={{ fontSize: '7.5px', color: '#94A3B8' }}>Real-time performance across all campuses</Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ─── STUDENT DASHBOARD MOCKUP ─────────────────────────────────────────────────
function StudentDashboardContent() {
  const STUDENT_SIDEBAR = [
    { label: 'Dashboard', active: true },
    { label: 'Exam Management', arrow: true },
    { label: 'Syllabus' },
    { label: 'Assignments' },
    { label: 'Homework' },
    { label: 'Online Classes', arrow: true },
    { label: 'Timetable', arrow: true },
    { label: 'Leaves & Holidays', arrow: true },
    { label: 'Communication', arrow: true },
  ];

  const UPCOMING_EXAMS = [
    { subject: 'Hindi', type: 'Objective', term: 'Quarterly', timing: 'In 0h', timingColor: '#EF4444', timingBg: '#FEE2E2', date: 'Jun 12, 2026 at 09:00 • 60 mins • Max: 100' },
    { subject: 'Telugu', type: 'Objective', term: 'Quarterly', timing: 'In 0h', timingColor: '#EF4444', timingBg: '#FEE2E2', date: 'Jun 12, 2026 at 11:00 • 60 mins • Max: 100' },
    { subject: 'Chemistry', type: 'Descriptive', term: 'Half yearly', timing: 'In 0h', timingColor: '#EF4444', timingBg: '#FEE2E2', date: 'Jun 12, 2026 at 09:00 • 60 mins • Max: 100' },
    { subject: 'IIT Biology', type: 'Descriptive', term: 'Half yearly', timing: 'Tomorrow', timingColor: '#d97706', timingBg: '#FEF3C7', date: 'Jun 13, 2026 at 09:00 • 60 mins • Max: 100' },
    { subject: 'IIT Chemistry', type: 'Descriptive', term: 'Half yearly', timing: 'In 2 days', timingColor: '#2563EB', timingBg: '#DBEAFE', date: 'Jun 14, 2026 at 09:00 • 60 mins • Max: 100' },
  ];

  const RESULTS = [
    { term: 'Half yearly', pct: 85, barColor: '#10B981', date: 'Jun 8, 2026 • 170/200 marks' },
    { term: 'Quarterly', pct: 71.5, barColor: '#7C63D8', date: 'Jun 1, 2026 • 286/400 marks' },
  ];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', minHeight: '380px' }}>
      <DashSidebar items={STUDENT_SIDEBAR} />
      <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#F8FAFC' }}>
        <DashTopBar initials="HP" userName="Harika Patel" />
        <DashHeroBanner name="Harika Patel" idCode="VIDS261000" badge="Student" />

        {/* Two-column content */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1.15fr 1fr', gap: '8px', px: '10px', mt: '8px', pb: '10px', flex: 1, overflow: 'hidden' }}>

          {/* Upcoming Exams */}
          <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '10px', p: '10px 12px', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '7px' }}>
              <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#111827' }}>Upcoming Exams</Typography>
              <Typography sx={{ fontSize: '7px', color: '#7C63D8', fontWeight: 600 }}>View All ›</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              {UPCOMING_EXAMS.map((exam, i) => (
                <Box key={i} sx={{ pb: '5px', borderBottom: i < UPCOMING_EXAMS.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '2px' }}>
                    <Typography sx={{ fontSize: '8.5px', fontWeight: 700, color: '#111827' }}>{exam.subject}</Typography>
                    <Box sx={{ px: '5px', py: '1px', border: '1px solid #E5E7EB', borderRadius: '100px' }}>
                      <Typography sx={{ fontSize: '6.5px', color: '#64748B' }}>{exam.type}</Typography>
                    </Box>
                    <Box sx={{ px: '5px', py: '1px', bgcolor: exam.timingBg, borderRadius: '100px' }}>
                      <Typography sx={{ fontSize: '6.5px', fontWeight: 700, color: exam.timingColor }}>{exam.timing}</Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: '7px', color: '#94A3B8' }}>{exam.term}</Typography>
                  <Typography sx={{ fontSize: '6.5px', color: '#CBD5E1' }}>{exam.date}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Recent Results */}
          <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '10px', p: '10px 12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '7px' }}>
              <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#111827' }}>Recent Results</Typography>
              <Typography sx={{ fontSize: '7px', color: '#7C63D8', fontWeight: 600 }}>View All ›</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {RESULTS.map((r, i) => (
                <Box key={i} sx={{ p: '10px', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '5px' }}>
                    <Typography sx={{ fontSize: '8px', color: '#64748B' }}>{r.term}</Typography>
                    <Box sx={{ width: 18, height: 18, bgcolor: '#F1F5F9', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ fontSize: '6.5px', fontWeight: 700, color: '#64748B' }}>P</Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '14px', fontWeight: 800, color: r.barColor, lineHeight: 1, mb: '5px' }}>{r.pct}%</Typography>
                  <Box sx={{ bgcolor: '#E5E7EB', borderRadius: '3px', height: '5px', mb: '4px' }}>
                    <Box sx={{ bgcolor: r.barColor, borderRadius: '3px', height: '5px', width: `${r.pct}%`, transition: 'width 1s ease' }} />
                  </Box>
                  <Typography sx={{ fontSize: '7px', color: '#94A3B8' }}>{r.date}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

        </Box>
      </Box>
    </Box>
  );
}

// ─── MOBILE SANDBOX COMPONENTS ───────────────────────────────────────────────

function MobileStatusBar({ dark = false }: { dark?: boolean }) {
  const c = dark ? '#111' : '#fff';
  const faint = dark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.35)';
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: '16px', pt: '32px', pb: '6px', flexShrink: 0 }}>
      <Typography sx={{ fontSize: '10px', fontWeight: 700, color: c, lineHeight: 1 }}>9:41</Typography>
      <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: '1.5px', alignItems: 'flex-end' }}>
          {[4, 6, 8, 10].map((h, i) => (
            <Box key={i} sx={{ width: '2.5px', height: `${h}px`, bgcolor: i < 3 ? c : faint, borderRadius: '1px' }} />
          ))}
        </Box>
        <Box sx={{ border: `1.5px solid ${dark ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.8)'}`, borderRadius: '2.5px', width: '20px', height: '10px', display: 'flex', alignItems: 'center', px: '1.5px', position: 'relative' }}>
          <Box sx={{ bgcolor: '#4ADE80', borderRadius: '1px', height: '6px', width: '60%' }} />
          <Box sx={{ position: 'absolute', right: '-3px', top: '50%', transform: 'translateY(-50%)', width: '2px', height: '5px', bgcolor: dark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.5)', borderRadius: '1px' }} />
        </Box>
      </Box>
    </Box>
  );
}

function PhoneFrame({ children, screenCount = 1, screenIdx = 0, onDotClick }: {
  children: React.ReactNode; screenCount?: number; screenIdx?: number; onDotClick?: (i: number) => void;
}) {
  return (
    <Box sx={{ width: '232px', mx: 'auto', background: 'linear-gradient(145deg, #2E2E2E, #0F0F0F)', borderRadius: '37px', p: '3px', boxShadow: '0 40px 80px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(255,255,255,0.12)', position: 'relative' }}>
      <Box sx={{ position: 'absolute', left: '-3px', top: '77px', width: '3px', height: '24px', bgcolor: '#1A1A1A', borderRadius: '2px 0 0 2px' }} />
      <Box sx={{ position: 'absolute', left: '-3px', top: '114px', width: '3px', height: '45px', bgcolor: '#1A1A1A', borderRadius: '2px 0 0 2px' }} />
      <Box sx={{ position: 'absolute', left: '-3px', top: '172px', width: '3px', height: '45px', bgcolor: '#1A1A1A', borderRadius: '2px 0 0 2px' }} />
      <Box sx={{ position: 'absolute', right: '-3px', top: '122px', width: '3px', height: '63px', bgcolor: '#1A1A1A', borderRadius: '0 2px 2px 0' }} />
      <Box sx={{ borderRadius: '35px', overflow: 'hidden', height: '472px', display: 'flex', flexDirection: 'column', position: 'relative', bgcolor: '#fff' }}>
        <Box sx={{ position: 'absolute', top: '9px', left: '50%', transform: 'translateX(-50%)', width: '76px', height: '19px', bgcolor: '#0F0F0F', borderRadius: '10px', zIndex: 20 }} />
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>{children}</Box>
        <Box sx={{ pb: '9px', pt: '5px', display: 'flex', justifyContent: 'center', gap: '6px', flexShrink: 0 }}>
          {screenCount > 1
            ? Array.from({ length: screenCount }).map((_, i) => (
                <Box key={i} onClick={() => onDotClick?.(i)} sx={{ width: i === screenIdx ? '16px' : '5px', height: '4px', borderRadius: '3px', bgcolor: i === screenIdx ? '#7C63D8' : '#CBD5E1', cursor: 'pointer', transition: 'all 0.3s' }} />
              ))
            : <Box sx={{ width: '80px', height: '4px', bgcolor: '#D1D5DB', borderRadius: '2px' }} />}
        </Box>
      </Box>
    </Box>
  );
}

const ADMIN_SCREENS = [
  '/mobile-screens/admin-1.jpg',
  '/mobile-screens/admin-2.jpg',
];

function AdminMobileContent({ screen }: { screen: number }) {
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
      <Box
        component="img"
        src={ADMIN_SCREENS[screen] ?? ADMIN_SCREENS[0]}
        alt={`Administrator screen ${screen + 1}`}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }}
      />
    </Box>
  );
}

const TEACHER_SCREENS = [
  '/mobile-screens/teacher-3.jpg',
  '/mobile-screens/teacher-2.jpg',
  '/mobile-screens/teacher-1.jpg',
];

function TeacherMobileContent({ screen }: { screen: number }) {
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
      {/* Extends the purple header area so the notch sits above all content */}
      <Box sx={{ height: '30px', flexShrink: 0, bgcolor: '#7335EB' }} />
      <Box component="img" src={TEACHER_SCREENS[screen] ?? TEACHER_SCREENS[0]} alt={`Teacher screen ${screen + 1}`}
        sx={{ flex: 1, width: '100%', minHeight: 0, objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
    </Box>
  );
}

const MGMT_SCREENS = [
  '/mobile-screens/mgmt-1.jpg',
  '/mobile-screens/mgmt-3.jpg',
  '/mobile-screens/mgmt-2.jpg',
];

function ManagementMobileContent({ screen }: { screen: number }) {
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
      <Box
        component="img"
        src={MGMT_SCREENS[screen] ?? MGMT_SCREENS[0]}
        alt={`Management screen ${screen + 1}`}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }}
      />
    </Box>
  );
}

function StudentMobileContent({ screen }: { screen: number }) {
  if (screen === 2) {
    const sylPatch = (top: string) => (
      <Box sx={{ position: 'absolute', top, left: '10px', right: '10px', bgcolor: '#fff', py: '2px' }}>
        <Typography sx={{ fontSize: '7px', color: '#374151', lineHeight: 1.4 }}>• Chapter 1</Typography>
      </Box>
    );
    return (
      <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
        {/* 10px spacer pushes exam content below the phone notch */}
        <Box sx={{ height: '10px', flexShrink: 0 }} />
        <Box component="img" src="/mobile-screens/student-3.jpg" alt="Student exam details screen"
          sx={{ flex: 1, width: '100%', minHeight: 0, objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
        {sylPatch('184px')}
        {sylPatch('314px')}
      </Box>
    );
  }
  if (screen === 1) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#F8FAFC', overflow: 'hidden' }}>
        <MobileStatusBar dark />
        <Box sx={{ px: '14px', flex: 1, overflow: 'hidden' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 800, color: '#111827', mb: '12px', fontFamily: '"Syne",sans-serif' }}>Fee & Events</Typography>
          <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: '12px', mb: '10px' }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#111827', mb: '8px' }}>Fee Status</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '8px' }}>
              {[{ label: 'Total', value: '₹45,000', color: '#111827' }, { label: 'Paid', value: '₹35,000', color: '#059669' }, { label: 'Pending', value: '₹10,000', color: '#DC2626' }].map(f => (
                <Box key={f.label} sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '11px', fontWeight: 800, color: f.color }}>{f.value}</Typography>
                  <Typography sx={{ fontSize: '8px', color: '#94A3B8' }}>{f.label}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ bgcolor: '#E5E7EB', borderRadius: '4px', height: '6px', mb: '4px' }}>
              <Box sx={{ bgcolor: '#059669', borderRadius: '4px', height: '6px', width: '78%' }} />
            </Box>
            <Typography sx={{ fontSize: '8.5px', color: '#94A3B8' }}>78% paid • Next: Term 3 ₹10,000 due Jul 15</Typography>
          </Box>
          <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: '12px' }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#111827', mb: '8px' }}>Upcoming Events</Typography>
            {[{ name: 'Parent-Teacher Meeting', date: 'Jun 20, 2026', color: '#7C63D8' }, { name: 'Annual Sports Day', date: 'Jun 28, 2026', color: '#059669' }, { name: 'Mid-Term Exams', date: 'Jul 5, 2026', color: '#D97706' }].map(e => (
              <Box key={e.name} sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '6px' }}>
                <Box sx={{ width: '4px', height: '30px', bgcolor: e.color, borderRadius: '2px', flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#111827' }}>{e.name}</Typography>
                  <Typography sx={{ fontSize: '8.5px', color: '#94A3B8' }}>{e.date}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
      <Box component="img" src="/mobile-screens/student-1.jpg" alt="Student home screen"
        sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
    </Box>
  );
}

// ─── NAVBAR ──────────────────────────────────────────────────────────────────
const HomeNavbar = ({ onOpenDemo, onLogin }: { onOpenDemo: () => void; onLogin: () => void }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navNavigate = useNavigate();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const smoothScroll = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    const wasOpen = mobileOpen;
    setMobileOpen(false);
    if (href.startsWith('#')) {
      // On mobile, wait for the drawer exit animation (220ms) before scrolling
      // so the layout is stable when we calculate the scroll target position.
      // Always subtract the fixed AppBar height (68px) + breathing room.
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, wasOpen ? 250 : 0);
    } else {
      navNavigate(href);
    }
  };

  return (
    <AppBar position="fixed" elevation={0} sx={{
      background: 'rgba(17,24,39,0.95)',
      backdropFilter: 'blur(16px)',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
      transition: 'all 0.3s ease',
      backgroundImage: 'none',
    }}>
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ height: 68, gap: 2 }}>
          {/* Logo */}
          <Box component="a" href="#" onClick={(e: React.MouseEvent) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            sx={{ textDecoration: 'none', flexGrow: { xs: 1, lg: 0 }, flexShrink: 0 }}>
            <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '24px', fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' }}>
              Skolar<Box component="span" sx={{ color: 'primary.main' }}>Minds</Box>
            </Typography>
          </Box>

          {/* Desktop nav links */}
          <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 4, flexGrow: 1, justifyContent: 'center' }}>
            {NAV_LINKS.map(link => (
              <Box key={link.label} component="a" href={link.href}
                onClick={(e: React.MouseEvent) => smoothScroll(e, link.href)}
                sx={{ color: 'rgba(255,255,255,0.65)', textDecoration: 'none', fontSize: '16px', fontWeight: 500, transition: 'color 0.2s', '&:hover': { color: '#fff' } }}>
                {link.label}
              </Box>
            ))}
          </Box>

          {/* Desktop CTAs */}
          {/* RESTORE: uncomment the two blocks below to put Why SkolarMinds + The Shift back in the navbar */}
          {/* <Button onClick={onOpenWhy} sx={{ px:'20px',py:'9px',borderRadius:'8px',fontSize:'14px',fontWeight:500,color:'#C4B5FD',background:'linear-gradient(#080C14,#080C14) padding-box,linear-gradient(135deg,#7C63D8,#A78BFA) border-box',border:'1px solid transparent','&:hover':{background:'linear-gradient(rgba(124,99,216,0.12),rgba(124,99,216,0.12)) padding-box,linear-gradient(135deg,#7C63D8,#A78BFA) border-box',color:'#fff'} }}>Why SkolarMinds</Button> */}
          {/* <Button onClick={onOpenShift} sx={{ px:'20px',py:'9px',borderRadius:'8px',fontSize:'14px',fontWeight:500,color:'#C4B5FD',background:'linear-gradient(#080C14,#080C14) padding-box,linear-gradient(135deg,#7C63D8,#A78BFA) border-box',border:'1px solid transparent','&:hover':{background:'linear-gradient(rgba(124,99,216,0.12),rgba(124,99,216,0.12)) padding-box,linear-gradient(135deg,#7C63D8,#A78BFA) border-box',color:'#fff'} }}>The Shift</Button> */}
          <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            <Button onClick={onLogin} sx={{
              px: '22px', py: '9px', bgcolor: 'transparent', borderRadius: '8px',
              color: 'rgba(255,255,255,0.8)', fontSize: '15px', fontWeight: 500,
              border: '1px solid rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', color: '#fff', borderColor: 'rgba(255,255,255,0.4)' },
            }}>Login</Button>
            <Button onClick={onOpenDemo} sx={{
              px: '22px', py: '9px', bgcolor: 'primary.main', borderRadius: '8px',
              color: '#fff', fontSize: '15px', fontWeight: 500,
              '&:hover': { bgcolor: 'primary.dark' },
            }}>Book a Demo</Button>
          </Box>

          {/* Mobile hamburger — visible below lg so tablet (md–lg) users always have menu access */}
          <IconButton onClick={() => setMobileOpen(p => !p)} sx={{ display: { lg: 'none' }, color: 'rgba(255,255,255,0.7)' }}>
            {mobileOpen ? <X size={22} /> : <MenuIcon size={22} />}
          </IconButton>
        </Toolbar>

        {/* Mobile drawer */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div key="mob" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }} style={{ overflow: 'hidden' }}>
              <Box sx={{ pb: 3, pt: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                {NAV_LINKS.map(link => (
                  <Box key={link.label} component="a" href={link.href}
                    onClick={(e: React.MouseEvent) => smoothScroll(e, link.href)}
                    sx={{ display: 'block', py: 1, px: 1, color: 'rgba(255,255,255,0.65)', textDecoration: 'none', fontSize: '15px', fontWeight: 500, '&:hover': { color: '#fff' } }}>
                    {link.label}
                  </Box>
                ))}
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* RESTORE: <Button fullWidth onClick={() => { setMobileOpen(false); onOpenShift(); }} sx={{ borderRadius:'8px',py:1,fontSize:'13px',color:'#C4B5FD',background:'linear-gradient(#080C14,#080C14) padding-box,linear-gradient(135deg,#7C63D8,#A78BFA) border-box',border:'1px solid transparent' }}>The Shift</Button> */}
                  <Button fullWidth onClick={() => { setMobileOpen(false); onLogin(); }}
                    sx={{ bgcolor: 'transparent', color: 'rgba(255,255,255,0.8)', borderRadius: '8px', py: 1, fontSize: '13px', border: '1px solid rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', color: '#fff' } }}>
                    Login
                  </Button>
                  <Button fullWidth onClick={() => { setMobileOpen(false); onOpenDemo(); }}
                    sx={{ bgcolor: 'primary.main', color: '#fff', borderRadius: '8px', py: 1, fontSize: '13px', '&:hover': { bgcolor: 'primary.dark' } }}>
                    Book Demo
                  </Button>
                </Box>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Container>
    </AppBar>
  );
};

// ─── DEMO MODAL ───────────────────────────────────────────────────────────────
const STATES = ['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal'];
const UNION_TERRITORIES = ['Andaman and Nicobar Islands','Chandigarh','Dadra and Nagar Haveli and Daman and Diu','Delhi (NCT)','Jammu and Kashmir','Ladakh','Lakshadweep','Puducherry'];
const DEMO_LABEL_SX = { fontSize: '0.7rem', fontWeight: 600, color: '#1E293B', mb: 0.75, display: 'block' };

const DemoModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({ state: false, usingErp: false });
  const [form, setForm] = useState({
    institutionName: '', studentCount: '', city: '', state: '',
    fullName: '', email: '', mobile: '', usingErp: '', demoDateTime: '', requirements: '',
  });

  React.useEffect(() => {
    if (open) {
      setIsSubmitted(false);
      setErrors({ state: false, usingErp: false });
      setForm({ institutionName: '', studentCount: '', city: '', state: '', fullName: '', email: '', mobile: '', usingErp: '', demoDateTime: '', requirements: '' });
    }
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = { state: !form.state, usingErp: !form.usingErp };
    setErrors(newErrors);
    if (newErrors.state || newErrors.usingErp) return;
    setIsSubmitted(true);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', backgroundImage: 'none', color: '#0F172A', maxHeight: '92vh' } }}>
      <Box sx={{ height: 5, bgcolor: '#7C63D8', borderRadius: '16px 16px 0 0' }} />
      <IconButton onClick={onClose} sx={{ position: 'absolute', top: 14, right: 14, color: '#94A3B8', '&:hover': { color: '#0F172A', bgcolor: 'rgba(0,0,0,0.05)' } }}>
        <X size={18} />
      </IconButton>
      <DialogContent sx={{ p: { xs: '24px 20px', md: '28px 32px' }, overflowY: 'auto', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 2 } }}>
        {!isSubmitted ? (
          <Box component="form" onSubmit={handleSubmit}>
            <Box sx={{ mb: 3, pb: 2.5, borderBottom: '1px solid #EEF2FF' }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#0F172A', fontFamily: '"Syne", sans-serif', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                Book Your <Box component="span" sx={{ color: '#7C63D8' }}>Free Demo</Box>
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#22C55E', flexShrink: 0, boxShadow: '0 0 0 3px rgba(34,197,94,0.2)' }} />
                <Typography sx={{ fontSize: '0.78rem', color: '#475569', fontWeight: 500 }}>Typically responds within 2 business hours.</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography sx={DEMO_LABEL_SX}>School / College Name *</Typography>
                <Box component="input" name="institutionName" type="text" required value={form.institutionName} onChange={handleChange} placeholder="Enter institution name" sx={mInputSx} />
              </Box>
              <Box>
                <Typography sx={DEMO_LABEL_SX}>Number of Students *</Typography>
                <Box component="input" name="studentCount" type="number" required value={form.studentCount} onChange={handleChange} placeholder="e.g. 1200" min="1" sx={mInputSx} />
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={DEMO_LABEL_SX}>City *</Typography>
                  <Box component="input" name="city" type="text" required value={form.city} onChange={handleChange} placeholder="Enter city" sx={mInputSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={DEMO_LABEL_SX}>State *</Typography>
                  <Select
                    name="state" value={form.state} displayEmpty
                    onChange={(e) => { setForm(prev => ({ ...prev, state: e.target.value })); setErrors(prev => ({ ...prev, state: false })); }}
                    sx={{
                      width: '100%', bgcolor: '#F8FAFC', borderRadius: '10px',
                      color: form.state ? '#0F172A' : '#64748B',
                      fontSize: '0.875rem', fontFamily: '"DM Sans", sans-serif',
                      '& .MuiOutlinedInput-notchedOutline': { border: errors.state ? '1px solid #F87171' : '1px solid #E2E8F0' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: errors.state ? '#F87171' : '#CBD5E1' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7C63D8', borderWidth: 1 },
                      '& .MuiSelect-select': { py: '10px', px: '14px' },
                      '& .MuiSelect-icon': { color: '#94A3B8' },
                    }}
                    MenuProps={{ PaperProps: { sx: { bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', mt: 0.5, boxShadow: '0 8px 24px rgba(0,0,0,0.08)', '& .MuiMenuItem-root': { fontSize: '0.875rem', color: '#374151', py: 0.875, '&:hover': { bgcolor: 'rgba(124,99,216,0.07)', color: '#7C63D8' }, '&.Mui-selected': { bgcolor: 'rgba(124,99,216,0.1)', color: '#7C63D8', '&:hover': { bgcolor: 'rgba(124,99,216,0.14)' } } }, '& .MuiListSubheader-root': { bgcolor: 'transparent', color: '#7C63D8', fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', lineHeight: '2.2', borderBottom: '1px solid rgba(124,99,216,0.15)', mb: 0.5 } } } }}
                  >
                    <MenuItem value="" disabled sx={{ color: '#475569', fontSize: '0.875rem' }}>Select state</MenuItem>
                    <ListSubheader>States</ListSubheader>
                    {STATES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    <ListSubheader>Union Territories</ListSubheader>
                    {UNION_TERRITORIES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                  {errors.state && <Typography sx={{ fontSize: '0.68rem', color: '#F87171', mt: 0.5 }}>Please select a state</Typography>}
                </Grid>
              </Grid>
              <Box>
                <Typography sx={DEMO_LABEL_SX}>Full Name *</Typography>
                <Box component="input" name="fullName" type="text" required value={form.fullName} onChange={handleChange} placeholder="Enter your full name" sx={mInputSx} />
              </Box>
              <Box>
                <Typography sx={DEMO_LABEL_SX}>Email Address *</Typography>
                <Box component="input" name="email" type="email" required value={form.email} onChange={handleChange} placeholder="name@school.com" sx={mInputSx} />
              </Box>
              <Box>
                <Typography sx={DEMO_LABEL_SX}>Mobile Number *</Typography>
                <Box component="input" name="mobile" type="tel" required value={form.mobile} onChange={handleChange} placeholder="Enter mobile number" sx={mInputSx} />
              </Box>
              <Box>
                <Typography sx={DEMO_LABEL_SX}>Using Other ERP? *</Typography>
                <Box sx={{ display: 'flex', gap: 1.5, mt: 0.25 }}>
                  {(['Yes', 'No'] as const).map(opt => (
                    <Box key={opt} onClick={() => { setForm(prev => ({ ...prev, usingErp: opt })); setErrors(prev => ({ ...prev, usingErp: false })); }}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', px: 2, py: 0.875, borderRadius: '8px', border: form.usingErp === opt ? '1px solid #7C63D8' : errors.usingErp ? '1px solid #F87171' : '1px solid #E2E8F0', bgcolor: form.usingErp === opt ? 'rgba(124,99,216,0.08)' : '#F8FAFC', transition: 'all 0.2s' }}>
                      <Box sx={{ width: 13, height: 13, borderRadius: '50%', border: form.usingErp === opt ? '4px solid #7C63D8' : '1.5px solid #CBD5E1', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.85rem', color: form.usingErp === opt ? '#7C63D8' : '#1E293B', fontWeight: 500 }}>{opt}</Typography>
                    </Box>
                  ))}
                </Box>
                {errors.usingErp && <Typography sx={{ fontSize: '0.68rem', color: '#F87171', mt: 0.5 }}>Please select an option</Typography>}
              </Box>
              <Box>
                <Typography sx={DEMO_LABEL_SX}>Preferred Demo Date & Time *</Typography>
                <Box sx={{ position: 'relative' }}>
                  <Box component="input" name="demoDateTime" type="datetime-local" required value={form.demoDateTime}
                    onChange={handleChange}
                    min={new Date().toISOString().slice(0, 16)}
                    sx={{ ...mInputSx, cursor: 'pointer', pr: '38px', '&::-webkit-calendar-picker-indicator': { opacity: 0, position: 'absolute', right: 0, top: 0, width: '100%', height: '100%', cursor: 'pointer' } }} />
                  <Box sx={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', pointerEvents: 'none' }}>
                    <Calendar size={15} />
                  </Box>
                </Box>
              </Box>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                  <Typography sx={DEMO_LABEL_SX}>Tell us about your requirements</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#374151' }}>{form.requirements.length}/500</Typography>
                </Box>
                <Box component="textarea" name="requirements" value={form.requirements}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm(prev => ({ ...prev, requirements: e.target.value }))}
                  maxLength={500} placeholder="Describe your specific needs, current challenges, or questions..."
                  rows={3} sx={{ ...mInputSx, resize: 'none', minHeight: '72px' }} />
              </Box>
              <Button type="submit"
                sx={{ bgcolor: '#7C63D8', color: 'white', fontWeight: 700, borderRadius: '10px', py: 1.4, fontSize: '0.9rem', mt: 0.5, '&:hover': { bgcolor: '#6B52D1' }, textTransform: 'none', width: '100%' }}>
                Book My Demo
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2.5, py: 3 }}>
            <Box sx={{ p: 2.5, bgcolor: '#DCFCE7', borderRadius: '50%', border: '1px solid #86EFAC', color: '#16A34A' }}>
              <CheckCircle2 size={48} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.65rem', fontFamily: 'monospace', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#16A34A' }}>Demo Booked Successfully</Typography>
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, color: '#0F172A', mt: 0.75 }}>We'll be in touch soon!</Typography>
              <Typography sx={{ fontSize: '0.825rem', color: '#374151', mt: 0.75 }}>Our team will contact <strong style={{ color: '#0F172A' }}>{form.email}</strong> within 2 business hours.</Typography>
            </Box>
            <Button onClick={onClose}
              sx={{ bgcolor: '#7C63D8', color: 'white', fontWeight: 600, px: 5, py: 1.5, borderRadius: '12px', '&:hover': { bgcolor: '#6B52D1' }, textTransform: 'none' }}>
              Return to Home
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── HOME INNER ───────────────────────────────────────────────────────────────
function HomeInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [demoOpen, setDemoOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:599px)');

  useEffect(() => {
    if ((location.state as { openDemo?: boolean } | null)?.openDemo) {
      setDemoOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const { scrollY } = useScroll();
  const sunOpacity = useTransform(scrollY, [0, 400], [1, 0]);
  const sunY = useTransform(scrollY, [0, 400], [0, 40]);
  const moonOpacity = useTransform(scrollY, [200, 500], [0, 1]);
  const moonY = useTransform(scrollY, [200, 500], [-30, 0]);
  const starsOpacity = useTransform(scrollY, [150, 450], [0, 1]);

  const [activeRoleSandbox, setActiveRoleSandbox] = useState('administrators');
  const [sandboxDevice, setSandboxDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [mobileScreenIdx, setMobileScreenIdx] = useState(0);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [slideDir, setSlideDir] = useState(1);
  const [activeModuleCategory, setActiveModuleCategory] = useState('academic');
  const [moduleExpanded, setModuleExpanded] = useState(false);
  const [hiwOpen, setHiwOpen] = useState({ institution: false, flexible: false });
  const activeCategory = MODULE_CATEGORIES.find(c => c.id === activeModuleCategory) || MODULE_CATEGORIES[0];
  const visibleModules = moduleExpanded ? activeCategory.modules : activeCategory.modules.slice(0, 4);
  const hiddenCount = activeCategory.modules.length - 4;

  const mockupWrapperRef = useRef<HTMLDivElement>(null);

  const handleMockupMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = mockupWrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    const rotY = (x / (rect.width / 2)) * 8;
    const rotX = -(y / (rect.height / 2)) * 5;
    el.style.transition = 'transform 0.1s ease-out, box-shadow 0.1s ease-out';
    el.style.transform = `perspective(1200px) rotateX(${rotX}deg) rotateY(${rotY}deg) translateY(-10px) scale(1.01)`;
    el.style.boxShadow = '0 60px 120px rgba(124,99,216,0.45), 0 20px 50px rgba(0,0,0,0.6)';
  };

  const handleMockupMouseLeave = () => {
    const el = mockupWrapperRef.current;
    if (!el) return;
    el.style.transition = 'transform 0.55s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.5s ease';
    el.style.transform = 'perspective(1200px) rotateX(0deg) rotateY(0deg) translateY(0px) scale(1)';
    el.style.boxShadow = '0 40px 90px rgba(124,99,216,0.3), 0 12px 32px rgba(0,0,0,0.5)';
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* RESTORE: add onOpenShift={() => navigate(ROUTES.PUBLIC.TRANSFORMATION)} onOpenWhy={() => navigate(ROUTES.PUBLIC.WHY)} to re-enable navbar buttons */}
      <HomeNavbar
        onOpenDemo={() => setDemoOpen(true)}
        onLogin={() => navigate(ROUTES.AUTH.LOGIN)}
      />

      {/* ── HERO ── */}
      <Box sx={{
        background: 'linear-gradient(160deg, #06090F 0%, #080C14 45%, #0C1020 80%, #090C17 100%)',
        height: 'calc(100vh - 44px)', display: 'flex', flexDirection: 'column',
        pt: { xs: '80px', md: '84px' }, pb: '280px',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Warm horizon atmosphere */}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '340px', zIndex: 1, pointerEvents: 'none', background: 'linear-gradient(to top, rgba(210,95,25,0.14) 0%, rgba(255,115,35,0.05) 38%, transparent 100%)' }} />

        {/* Stars — appear on scroll */}
        <MotionBox style={{ opacity: starsOpacity }} sx={{ position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none' }}>
          {STARS.map((s, i) => (
            <Box key={i} sx={{
              position: 'absolute', left: `${s.x}%`, top: `${s.y}%`,
              width: `${s.s * 2}px`, height: `${s.s * 2}px`,
              borderRadius: '50%', bgcolor: '#fff',
              '@keyframes skolarTwinkle': {
                '0%,100%': { opacity: 0.85, transform: 'scale(1)' },
                '50%': { opacity: 0.12, transform: 'scale(0.35)' },
              },
              animation: `skolarTwinkle ${s.dur} ease-in-out ${s.delay} infinite`,
            }}/>
          ))}
        </MotionBox>

        {/* Sun — fades and dips as you scroll */}
        <MotionBox style={{ opacity: sunOpacity, y: sunY }} sx={{ position: 'absolute', top: '125px', left: 'calc(79.9% - 38px)', zIndex: 2, pointerEvents: 'none', width: '76px', height: '76px' }}>
          <Box sx={{ position: 'absolute', inset: '-26px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,140,50,0.26) 0%, rgba(255,100,20,0.08) 52%, transparent 74%)' }}/>
          <svg width="76" height="76" viewBox="0 0 76 76" style={{ display: 'block' }}>
            <circle cx="38" cy="38" r="30" fill="#FF8C42" opacity="0.75"/>
            <circle cx="38" cy="38" r="21" fill="#FFC170" opacity="0.88"/>
          </svg>
        </MotionBox>

        {/* Moon — rises on scroll */}
        <MotionBox style={{ opacity: moonOpacity, y: moonY }} sx={{ position: 'absolute', bottom: '310px', left: '85%', zIndex: 2, pointerEvents: 'none', width: '80px', height: '80px' }}>
          <Box sx={{ position: 'absolute', inset: '-20px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(200,190,255,0.15) 0%, transparent 68%)' }}/>
          <svg width="80" height="80" viewBox="0 0 80 80" style={{ display: 'block' }}>
            <defs>
              <mask id="skolarMoonMask">
                <rect width="80" height="80" fill="white"/>
                <circle cx="53" cy="33" r="29" fill="black"/>
              </mask>
            </defs>
            <circle cx="40" cy="40" r="32" fill="#E8DFFF" opacity="0.88" mask="url(#skolarMoonMask)"/>
          </svg>
        </MotionBox>

        {/* Stars */}
        {[
          { top: '8%',  left: '10%', size: 3,   delay: '0s',    dur: '3.1s' },
          { top: '14%', left: '22%', size: 2.5, delay: '1.4s',  dur: '2.7s' },
          { top: '6%',  left: '36%', size: 3,   delay: '0.8s',  dur: '3.5s' },
          { top: '18%', left: '48%', size: 2.5, delay: '2.0s',  dur: '2.4s' },
          { top: '9%',  left: '58%', size: 3,   delay: '0.5s',  dur: '3.8s' },
          { top: '20%', left: '65%', size: 2.5, delay: '1.8s',  dur: '2.9s' },
          { top: '7%',  left: '72%', size: 3,   delay: '1.1s',  dur: '3.3s' },
          { top: '16%', left: '80%', size: 2.5, delay: '2.5s',  dur: '2.6s' },
          { top: '10%', left: '88%', size: 3,   delay: '0.3s',  dur: '4.0s' },
          { top: '22%', left: '93%', size: 2.5, delay: '1.6s',  dur: '3.2s' },
          { top: '5%',  left: '54%', size: 2.5, delay: '2.2s',  dur: '2.8s' },
          { top: '3%',  left: '5%',  size: 2,   delay: '0.6s',  dur: '3.4s' },
          { top: '12%', left: '15%', size: 1.5, delay: '1.9s',  dur: '2.5s' },
          { top: '25%', left: '30%', size: 2,   delay: '0.4s',  dur: '4.1s' },
          { top: '4%',  left: '42%', size: 1.5, delay: '2.7s',  dur: '2.9s' },
          { top: '28%', left: '55%', size: 2,   delay: '1.0s',  dur: '3.6s' },
          { top: '3%',  left: '68%', size: 1.5, delay: '2.3s',  dur: '2.6s' },
          { top: '24%', left: '75%', size: 2,   delay: '0.7s',  dur: '3.9s' },
          { top: '13%', left: '84%', size: 1.5, delay: '1.5s',  dur: '3.0s' },
          { top: '30%', left: '90%', size: 2,   delay: '0.2s',  dur: '4.3s' },
          { top: '19%', left: '97%', size: 1.5, delay: '1.2s',  dur: '2.8s' },
          { top: '8%',  left: '3%',  size: 1.5, delay: '2.9s',  dur: '3.7s' },
          { top: '27%', left: '44%', size: 2,   delay: '0.9s',  dur: '3.2s' },
        ].map((s, i) => (
          <Box key={`star-${i}`} sx={{
            position: 'absolute', top: s.top, left: s.left, zIndex: 2, pointerEvents: 'none',
            width: `${s.size}px`, height: `${s.size}px`, borderRadius: '50%', bgcolor: '#fff',
            '@keyframes skolarStarBlink': { '0%': { opacity: 0.9 }, '50%': { opacity: 0.1 }, '100%': { opacity: 0.9 } },
            animation: `skolarStarBlink ${s.dur} ease-in-out ${s.delay} infinite`,
          }} />
        ))}

        {/* Radial gradient — text readability */}
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 4, background: 'radial-gradient(ellipse 85% 65% at 72% 50%, rgba(124,99,216,0.30) 0%, transparent 68%), radial-gradient(ellipse 40% 45% at 76% 52%, rgba(167,139,250,0.12) 0%, transparent 52%)', pointerEvents: 'none' }} />

        {/* School skyline */}
        <SchoolSkyline />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 5, ml: 0, pl: { xs: 3, md: '56px' } }}>
          <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">

            {/* Left — Hero text */}
            <Grid item xs={12} md={7}>
              <Typography variant="h1" sx={{ fontSize: 'clamp(30px, 4vw, 56px)', color: '#fff', mb: 2, textAlign: 'left' }}>
                Your Institution's<br />
                <Box component="em" sx={{ fontStyle: 'normal', background: 'linear-gradient(135deg, #A78BFA, #7C63D8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  Digital Foundation
                </Box>
                <br />for the Future
              </Typography>
              <Typography sx={{ fontSize: 'clamp(14px, 1.4vw, 17px)', color: 'rgba(255,255,255,0.65)', fontWeight: 300, lineHeight: 1.7, textAlign: 'left', maxWidth: '520px' }}>
                Modern institutions don't just need tools. They need a connected ecosystem that empowers leadership, elevates every experience, and scales with ambition.
              </Typography>
            </Grid>

          </Grid>
        </Container>
      </Box>

      {/* ── TICKER ── */}
      <Box sx={{
        bgcolor: '#080C14',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        py: '15px', overflow: 'hidden', position: 'relative',
        '&::before': { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: '100px', zIndex: 2, background: 'linear-gradient(90deg, #080C14, transparent)' },
        '&::after': { content: '""', position: 'absolute', right: 0, top: 0, bottom: 0, width: '100px', zIndex: 2, background: 'linear-gradient(270deg, #080C14, transparent)' },
      }}>
        <Box sx={{
          display: 'flex', gap: '48px', whiteSpace: 'nowrap', width: 'max-content',
          '@keyframes skolarHomeTicker': { '0%': { transform: 'translateX(0)' }, '100%': { transform: 'translateX(-50%)' } },
          animation: 'skolarHomeTicker 28s linear infinite',
        }}>
          {[...HOME_TICKER, ...HOME_TICKER].map((item, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#7C63D8' }} />
              <Typography sx={{ fontSize: '16px', color: 'rgba(255,255,255,0.85)', fontWeight: 500, letterSpacing: '0.04em', fontFamily: '"DM Sans", sans-serif' }}>
                {item}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── CONNECTED PLATFORM ── */}
      <Box id="vision" sx={{
        background: 'linear-gradient(150deg, #F5F3FF 0%, #EEF2FF 55%, #F8FAFC 100%)',
        py: '100px', position: 'relative', overflow: 'hidden',
      }}>
        {/* Decorative blobs */}
        <Box sx={{ position: 'absolute', top: '-180px', right: '-150px', width: '520px', height: '520px', background: 'radial-gradient(circle, rgba(124,99,216,0.09), transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: '-120px', left: '-100px', width: '420px', height: '420px', background: 'radial-gradient(circle, rgba(99,102,241,0.07), transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%,-50%)', width: '700px', height: '300px', background: 'radial-gradient(ellipse, rgba(124,99,216,0.04), transparent 70%)', pointerEvents: 'none' }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Reveal>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              {/* Dot-separated label */}
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
                {['ONE PLATFORM', 'EVERY DEPARTMENT', 'EVERY STAKEHOLDER'].map((t, i) => (
                  <React.Fragment key={t}>
                    {i > 0 && <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#7C63D8', flexShrink: 0 }} />}
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.16em', color: '#7C63D8', fontFamily: '"DM Sans", sans-serif' }}>
                      {t}
                    </Typography>
                  </React.Fragment>
                ))}
              </Box>
              <Typography variant="h2" sx={{ fontSize: 'clamp(30px, 4vw, 50px)', color: '#111827', mb: 2.5 }}>
                Run Your Entire Institution<br />From One Connected Platform
              </Typography>
              <Typography sx={{ fontSize: '17px', color: '#475569', maxWidth: '540px', mx: 'auto', lineHeight: 1.7, fontWeight: 300 }}>
                Admissions, academics, fees, communication and operations — working together in real time.
              </Typography>
            </Box>
          </Reveal>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '20px', alignItems: 'start' }}>
            {PLATFORM_CARDS.map((card, i) => (
              <Reveal key={card.num} delay={i * 0.07}>
                <Box sx={{
                  bgcolor: '#fff',
                  border: '1.5px solid rgba(124,99,216,0.11)',
                  borderRadius: '20px',
                  p: '28px 22px 24px',
                  height: '100%',
                  display: 'flex', flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'rgba(124,99,216,0.30)',
                    boxShadow: '0 12px 40px rgba(124,99,216,0.10)',
                    transform: 'translateY(-5px)',
                  },
                }}>
                  {/* Number badge */}
                  <Box sx={{ display: 'inline-flex', alignSelf: 'flex-start', px: '12px', py: '5px', bgcolor: '#7C63D8', borderRadius: '100px', mb: 2.5 }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#fff', letterSpacing: '0.1em', fontFamily: '"DM Sans", sans-serif' }}>
                      {card.num}
                    </Typography>
                  </Box>

                  {/* Title */}
                  <Typography variant="h3" sx={{ fontSize: '17px', fontWeight: 700, color: '#111827', mb: 1.25, letterSpacing: '-0.3px', lineHeight: 1.25 }}>
                    {card.title}
                  </Typography>

                  {/* Body */}
                  <Typography sx={{ fontSize: '13.5px', color: '#475569', lineHeight: 1.65, mb: 2.5, fontWeight: 300, flex: 1 }}>
                    {card.body}
                  </Typography>

                  {/* Divider */}
                  <Box sx={{ height: '1px', bgcolor: 'rgba(124,99,216,0.1)', mb: 2 }} />

                  {/* Sub-features */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {card.features.map(({ Icon, label }) => (
                      <Box key={label} sx={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        px: '10px', py: '8px', borderRadius: '10px',
                        border: '1px solid transparent',
                        cursor: 'default',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: '#F3F0FF',
                          borderColor: 'rgba(124,99,216,0.18)',
                        },
                        '&:hover .feat-icon-box': {
                          bgcolor: '#DDD6FE',
                          transform: 'scale(1.14) rotate(-4deg)',
                        },
                      }}>
                        <Box className="feat-icon-box" sx={{
                          width: 32, height: 32, borderRadius: '8px',
                          bgcolor: '#EDE9FF',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'all 0.22s ease',
                        }}>
                          <Icon size={15} color="#7C63D8" />
                        </Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#374151' }}>
                          {label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Reveal>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── MODULES ── */}
      <Box id="modules" sx={{ bgcolor: '#F8FAFC', py: '100px' }}>
        <Container maxWidth="lg">
          <Reveal>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#7C63D8', mb: 2 }}>
                Full Spectrum
              </Typography>
              <Typography variant="h2" sx={{ fontSize: 'clamp(32px, 4vw, 52px)', color: '#111827', mb: 2.5 }}>
                Every Dimension of<br />Institutional Life, Covered
              </Typography>
              <Typography sx={{ fontSize: '18px', color: '#475569', maxWidth: '600px', mx: 'auto', lineHeight: 1.65, fontWeight: 300 }}>
                20 modules built around how institutions actually operate — grouped so you find what matters to you.
              </Typography>
            </Box>
          </Reveal>

          {/* Category filter tabs */}
          <Reveal>
            <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1.5, mb: 6 }}>
              {MODULE_CATEGORIES.map(cat => {
                const active = activeModuleCategory === cat.id;
                return (
                  <Box
                    key={cat.id}
                    onClick={() => { setActiveModuleCategory(cat.id); setModuleExpanded(false); }}
                    sx={{
                      px: '20px', py: '9px', borderRadius: '100px', cursor: 'pointer',
                      fontSize: '14px', fontWeight: 600, fontFamily: '"DM Sans", sans-serif',
                      transition: 'all 0.2s',
                      bgcolor: active ? '#7C63D8' : 'transparent',
                      color: active ? '#fff' : '#475569',
                      border: active ? '1.5px solid #7C63D8' : '1.5px solid #E2E8F0',
                      userSelect: 'none',
                      '&:hover': { borderColor: '#7C63D8', color: active ? '#fff' : '#7C63D8' },
                    }}
                  >
                    {cat.label}
                  </Box>
                );
              })}
            </Box>
          </Reveal>

          {/* Module cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: '20px' }}>
            {visibleModules.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.06}>
                <Box sx={{
                  bgcolor: m.featured ? undefined : '#FFFFFF',
                  background: m.featured ? 'linear-gradient(135deg, #F3F0FF, #fff)' : undefined,
                  border: m.featured ? '1.5px solid rgba(124,99,216,0.3)' : '1.5px solid #E5E7EB',
                  borderRadius: '16px', p: '24px',
                  position: 'relative', overflow: 'hidden', transition: 'all 0.3s', height: '100%',
                  '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #7C63D8, #A78BFA)', opacity: m.featured ? 1 : 0, transition: 'opacity 0.3s' },
                  '&:hover': { borderColor: 'rgba(124,99,216,0.35)', transform: 'translateY(-3px)', boxShadow: '0 12px 40px rgba(0,0,0,0.08)', '&::before': { opacity: 1 } },
                  '&:hover .card-icon': { transform: 'scale(1.18) translateY(-4px) rotate(6deg)' },
                }}>
                  <Box className="card-icon" sx={{ width: 46, height: 46, bgcolor: m.featured ? 'rgba(124,99,216,0.12)' : '#F3F0FF', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', mb: 2, transition: 'transform 0.3s cubic-bezier(0.34,1.56,0.64,1)' }}>
                    {m.icon}
                  </Box>
                  <Typography variant="h3" sx={{ fontSize: '16px', fontWeight: 700, color: '#111827', mb: 1 }}>
                    {m.title}
                  </Typography>
                  <Typography sx={{ fontSize: '13.5px', color: '#475569', lineHeight: 1.65 }}>
                    {m.lead}
                  </Typography>
                </Box>
              </Reveal>
            ))}
          </Box>

          {/* Expand / collapse */}
          {hiddenCount > 0 && (
            <Reveal>
              <Box sx={{ textAlign: 'center', mt: 5 }}>
                <Box
                  onClick={() => setModuleExpanded(e => !e)}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 1,
                    px: '28px', py: '11px', borderRadius: '100px', cursor: 'pointer',
                    border: '1.5px solid #E2E8F0', color: '#475569',
                    fontSize: '14px', fontWeight: 600, fontFamily: '"DM Sans", sans-serif',
                    transition: 'all 0.2s', userSelect: 'none',
                    '&:hover': { borderColor: '#7C63D8', color: '#7C63D8' },
                  }}
                >
                  {moduleExpanded ? 'Show less ↑' : `Show ${hiddenCount} more ↓`}
                </Box>
              </Box>
            </Reveal>
          )}
        </Container>
      </Box>

      {/* ── ROLE SANDBOX ── */}
      <Box id="roles" sx={{
        background: 'linear-gradient(160deg, #0D1117 0%, #0F172A 55%, #150E2E 100%)',
        py: '100px', position: 'relative', overflow: 'hidden',
      }}>
        {/* Ambient orbs */}
        <Box sx={{ position: 'absolute', top: '-160px', right: '-160px', width: '550px', height: '550px', background: 'radial-gradient(circle, rgba(124,99,216,0.1), transparent 68%)', borderRadius: '50%', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: '-120px', left: '-120px', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(124,99,216,0.07), transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Reveal>
            <Box sx={{ textAlign: 'center', mb: 7 }}>
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: '7px',
                bgcolor: 'rgba(124,99,216,0.1)', border: '1px solid rgba(124,99,216,0.28)',
                borderRadius: '100px', px: '16px', py: '6px', mb: 3,
              }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#A78BFA', flexShrink: 0, boxShadow: '0 0 0 3px rgba(167,139,250,0.2)' }} />
                <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#A78BFA' }}>
                  Interactive Preview
                </Typography>
              </Box>
              <Typography variant="h2" sx={{ fontSize: 'clamp(34px, 4.5vw, 58px)', color: '#fff', mb: 2.5, lineHeight: 1.08 }}>
                Experience the{' '}
                <Box component="span" sx={{
                  background: 'linear-gradient(135deg, #A78BFA 0%, #7C63D8 55%, #A78BFA 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  Role Sandbox
                </Box>
              </Typography>
              <Typography sx={{ fontSize: '18px', color: 'rgba(255,255,255,0.5)', maxWidth: '520px', mx: 'auto', lineHeight: 1.65, fontWeight: 300 }}>
                Step into any role and see exactly how SkolarMinds feels for each person in your institution.
              </Typography>
            </Box>
          </Reveal>

          <Grid container spacing={5} alignItems="flex-start">

            {/* Left — role tabs */}
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {ROLE_SANDBOX.map(role => {
                  const active = activeRoleSandbox === role.id;
                  return (
                    <Box key={role.id} onClick={() => { setActiveRoleSandbox(role.id); setMobileScreenIdx(0); }} sx={{
                      p: '16px 20px', borderRadius: '14px', cursor: 'pointer',
                      border: '1.5px solid', transition: 'all 0.25s',
                      borderColor: active ? '#7C63D8' : 'rgba(255,255,255,0.1)',
                      bgcolor: active ? '#7C63D8' : 'rgba(255,255,255,0.04)',
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      boxShadow: active ? '0 8px 28px rgba(124,99,216,0.35)' : 'none',
                      transform: active ? 'translateX(4px)' : 'translateX(0)',
                      '&:hover': { borderColor: '#7C63D8', transform: 'translateX(4px)', bgcolor: active ? '#7C63D8' : 'rgba(124,99,216,0.1)' },
                    }}>
                      <Box sx={{ width: 38, height: 38, borderRadius: '50%', flexShrink: 0, bgcolor: active ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '17px' }}>
                        {role.icon}
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
                          {role.label}
                        </Typography>
                        <Typography sx={{ fontSize: '11.5px', color: active ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.38)', mt: 0.25 }}>
                          {role.desc}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Grid>

            {/* Right — mockup with device toggle */}
            <Grid item xs={12} md={9}>
              {/* Device toggle */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2.5, gap: '6px' }}>
                {(['desktop', 'mobile'] as const).map(d => (
                  <Box key={d} onClick={() => { setSandboxDevice(d); setMobileScreenIdx(0); }} sx={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    px: '14px', py: '7px', borderRadius: '10px', cursor: 'pointer',
                    border: '1.5px solid',
                    borderColor: sandboxDevice === d ? '#7C63D8' : 'rgba(255,255,255,0.12)',
                    bgcolor: sandboxDevice === d ? 'rgba(124,99,216,0.15)' : 'rgba(255,255,255,0.04)',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: '#7C63D8', bgcolor: 'rgba(124,99,216,0.1)' },
                  }}>
                    {d === 'desktop' ? <Monitor size={14} color={sandboxDevice === d ? '#A78BFA' : 'rgba(255,255,255,0.45)'} /> : <Smartphone size={14} color={sandboxDevice === d ? '#A78BFA' : 'rgba(255,255,255,0.45)'} />}
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: sandboxDevice === d ? '#A78BFA' : 'rgba(255,255,255,0.45)', textTransform: 'capitalize' }}>{d}</Typography>
                  </Box>
                ))}
              </Box>

              <AnimatePresence mode="wait">
                <MotionBox
                  key={`${activeRoleSandbox}-${sandboxDevice}`}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.28, ease: 'easeOut' }}
                >
                  {sandboxDevice === 'desktop' ? (
                    <Box
                      ref={mockupWrapperRef}
                      onMouseMove={handleMockupMouseMove}
                      onMouseLeave={handleMockupMouseLeave}
                      sx={{ borderRadius: '16px', boxShadow: '0 40px 90px rgba(124,99,216,0.3), 0 12px 32px rgba(0,0,0,0.5)', willChange: 'transform' }}
                    >
                      <Box sx={{ borderRadius: '16px', overflow: 'hidden', border: '5px solid #0F172A' }}>
                        <Box sx={{ bgcolor: '#0F172A', px: 2.5, py: '10px', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ display: 'flex', gap: '5px', flexShrink: 0 }}>
                            <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: '#FF5F56' }} />
                            <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: '#FFBD2E' }} />
                            <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: '#27C93F' }} />
                          </Box>
                          <Box sx={{ flex: 1, mx: 3, bgcolor: 'rgba(255,255,255,0.07)', borderRadius: '6px', py: '4px', px: 2, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', fontFamily: 'monospace', letterSpacing: '0.02em' }}>
                              skolarminds.com/app/dashboard
                            </Typography>
                          </Box>
                        </Box>
                        {activeRoleSandbox === 'administrators' ? <AdminDashboardContent /> :
                         activeRoleSandbox === 'teachers' ? <TeacherDashboardContent /> :
                         activeRoleSandbox === 'management' ? <ManagementDashboardContent /> :
                         <StudentDashboardContent />}
                      </Box>
                    </Box>
                  ) : (() => {
                    const screenCount = activeRoleSandbox === 'management' ? 3 : activeRoleSandbox === 'families' ? 3 : activeRoleSandbox === 'teachers' ? 3 : 2;
                    const canPrev = mobileScreenIdx > 0;
                    const canNext = mobileScreenIdx < screenCount - 1;
                    const arrowBtn = (enabled: boolean) => ({
                      width: '36px', height: '36px', borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: '1.5px solid',
                      borderColor: enabled ? 'rgba(124,99,216,0.45)' : 'rgba(255,255,255,0.08)',
                      bgcolor: enabled ? 'rgba(124,99,216,0.18)' : 'rgba(255,255,255,0.04)',
                      color: enabled ? '#A78BFA' : 'rgba(255,255,255,0.2)',
                      cursor: enabled ? 'pointer' : 'default',
                      transition: 'all 0.2s',
                      flexShrink: 0,
                      ...(enabled ? { '&:hover': { bgcolor: 'rgba(124,99,216,0.32)', borderColor: '#A78BFA' } } : {}),
                    });
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <Box sx={arrowBtn(canPrev)} onClick={() => canPrev && setMobileScreenIdx(i => i - 1)}>
                          <ChevronLeft size={18} />
                        </Box>
                        <PhoneFrame
                          screenCount={screenCount}
                          screenIdx={mobileScreenIdx}
                          onDotClick={setMobileScreenIdx}
                        >
                          {activeRoleSandbox === 'administrators' ? <AdminMobileContent screen={mobileScreenIdx} /> :
                           activeRoleSandbox === 'teachers' ? <TeacherMobileContent screen={mobileScreenIdx} /> :
                           activeRoleSandbox === 'management' ? <ManagementMobileContent screen={mobileScreenIdx} /> :
                           <StudentMobileContent screen={mobileScreenIdx} />}
                        </PhoneFrame>
                        <Box sx={arrowBtn(canNext)} onClick={() => canNext && setMobileScreenIdx(i => i + 1)}>
                          <ChevronRight size={18} />
                        </Box>
                      </Box>
                    );
                  })()}
                </MotionBox>
              </AnimatePresence>
            </Grid>

          </Grid>
        </Container>
      </Box>

      {/* ── PARTNERSHIP ── */}
      <Box id="partnership" sx={{ bgcolor: '#F8FAFC', py: '100px' }}>
        <Container maxWidth="lg">

          {/* Header */}
          <Reveal>
            <Box sx={{ textAlign: 'center', mb: 7 }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#7C63D8', mb: 2 }}>
                Partnership &amp; Pricing Model
              </Typography>
              <Typography variant="h2" sx={{ fontSize: 'clamp(32px, 4vw, 52px)', color: '#111827', mb: 2.5 }}>
                One Platform.<br />Two Partnership Models.
              </Typography>
              <Typography sx={{ fontSize: '15px', color: '#475569', maxWidth: '600px', mx: 'auto', lineHeight: 1.7 }}>
                Some institutions prefer centralized digital management. Others prefer flexible digital adoption. With SkolarMinds,{' '}
                <Box component="strong" sx={{ color: '#111827', fontWeight: 600 }}>both paths lead to the same powerful platform, continuous innovation, and long-term partnership.</Box>
              </Typography>
            </Box>
          </Reveal>

          {/* Quote */}
          <Reveal>
            <Box sx={{
              maxWidth: '720px', mx: 'auto', mb: 7,
              p: '28px 36px',
              background: 'linear-gradient(135deg, #2D1F6E, #1E293B)',
              borderRadius: '20px',
              borderLeft: '4px solid #7C63D8',
            }}>
              <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: 'clamp(17px, 2vw, 21px)', fontWeight: 600, color: '#fff', lineHeight: 1.5, fontStyle: 'italic' }}>
                "We don't force institutions into our business model. We adapt our platform to yours."
              </Typography>
              <Typography sx={{ display: 'block', mt: 1.25, fontSize: '13px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.05em', fontStyle: 'normal' }}>
                — SkolarMinds Partnership Philosophy
              </Typography>
            </Box>
          </Reveal>

          {/* Comparison Table */}
          <Reveal>
            <Box sx={{ mb: 7, overflowX: 'auto', borderRadius: '20px', WebkitOverflowScrolling: 'touch' }}>
              <Box sx={{ borderRadius: '20px', border: '1.5px solid #E5E7EB', bgcolor: '#FFFFFF', overflow: 'hidden', minWidth: '560px' }}>
              {/* Header row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '34% 1fr 1fr', borderBottom: '1.5px solid #E5E7EB' }}>
                <Box sx={{ p: '20px 24px' }} />
                <Box sx={{ p: '20px 24px', bgcolor: 'rgba(124,99,216,0.06)' }}>
                  <Typography sx={{ fontFamily: '"Syne", sans-serif', fontWeight: 700, fontSize: '15px', color: '#7C63D8' }}>Institution Partnership</Typography>
                </Box>
                <Box sx={{ p: '20px 24px' }}>
                  <Typography sx={{ fontFamily: '"Syne", sans-serif', fontWeight: 700, fontSize: '15px', color: '#111827' }}>Flexible Partnership</Typography>
                </Box>
              </Box>
              {/* Body rows */}
              {PARTNERSHIP_TABLE.map((row, i) => (
                <Box key={row.feature} sx={{ display: 'grid', gridTemplateColumns: '34% 1fr 1fr', borderBottom: i < PARTNERSHIP_TABLE.length - 1 ? '1px solid #E5E7EB' : 'none', '&:hover': { bgcolor: 'rgba(124,99,216,0.02)' }, transition: 'background 0.2s' }}>
                  <Box sx={{ p: '16px 24px', display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '14.5px', fontWeight: 500, color: '#111827', lineHeight: 1.5 }}>{row.feature}</Typography>
                  </Box>
                  {/* Column A */}
                  <Box sx={{ p: '16px 24px', bgcolor: 'rgba(124,99,216,0.03)', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    {row.a.check && <Typography component="span" sx={{ color: '#7C63D8', fontSize: '17px', fontWeight: 700, lineHeight: 1 }}>✓</Typography>}
                    {row.a.tag && <Box component="span" sx={{ px: '10px', py: '3px', bgcolor: '#F3F0FF', borderRadius: '100px', fontSize: '12px', fontWeight: 500, color: '#7C63D8' }}>{row.a.tag}</Box>}
                    {row.a.text && <Typography component="span" sx={{ fontSize: '14.5px', color: '#475569' }}>{row.a.text}</Typography>}
                  </Box>
                  {/* Column B */}
                  <Box sx={{ p: '16px 24px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    {row.b.check && <Typography component="span" sx={{ color: '#7C63D8', fontSize: '17px', fontWeight: 700, lineHeight: 1 }}>✓</Typography>}
                    {row.b.tag && <Box component="span" sx={{ px: '10px', py: '3px', bgcolor: '#F3F0FF', borderRadius: '100px', fontSize: '12px', fontWeight: 500, color: '#7C63D8' }}>{row.b.tag}</Box>}
                    {row.b.text && <Typography component="span" sx={{ fontSize: '14.5px', color: '#475569' }}>{row.b.text}</Typography>}
                  </Box>
                </Box>
              ))}
              </Box>
            </Box>
          </Reveal>

          {/* Two cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '24px', mb: 7 }}>

            {/* Card A — Institution Partnership (dark/featured) */}
            <Reveal>
              <Box sx={{ p: '36px 32px', borderRadius: '20px', background: 'linear-gradient(135deg, #2D1F6E, #1E293B)', border: '1.5px solid rgba(124,99,216,0.5)', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'inline-block', px: '12px', py: '4px', bgcolor: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '100px', fontSize: '11px', fontWeight: 600, color: '#F59E0B', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 2, alignSelf: 'flex-start' }}>
                  Centralized Approach
                </Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#A78BFA', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5 }}>Institution Partnership</Typography>
                <Typography variant="h3" sx={{ fontSize: '21px', color: '#fff', mb: 1.5 }}>Complete Digital Control</Typography>
                <Typography sx={{ fontSize: '14.5px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.65 }}>
                  A centralized approach where the institution provides a connected digital experience for management, faculty, employees, students, and parents through one intelligent platform.
                </Typography>
                <Typography sx={{ fontSize: '13.5px', color: '#C4B5FD', fontWeight: 600, mt: 2 }}>
                  Perfect for institutions that prefer a unified digital ecosystem.
                </Typography>
                <Box
                  onClick={() => setHiwOpen(s => ({ ...s, institution: !s.institution }))}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', mt: '20px', fontSize: '14px', fontWeight: 600, color: '#C4B5FD', cursor: 'pointer', userSelect: 'none', transition: 'gap 0.2s', '&:hover': { gap: '10px' }, alignSelf: 'flex-start' }}
                >
                  How It Works
                  <Box component="span" sx={{ display: 'inline-block', transition: 'transform 0.3s', transform: hiwOpen.institution ? 'rotate(90deg)' : 'none' }}>→</Box>
                </Box>
                {hiwOpen.institution && (
                  <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <Box sx={{ textAlign: 'center', fontSize: '13px', mb: 3 }}>
                      {[['Institution'], ['Management', 'Faculty', 'Students', 'Parents'], ['One Unified SkolarMinds Platform']].map((group, gi) => (
                        <Box key={gi}>
                          {gi > 0 && <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '18px', my: '4px' }}>↓</Typography>}
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {group.map(label => (
                              <Box key={label} sx={{ px: '20px', py: '8px', bgcolor: 'rgba(124,99,216,0.15)', border: '1px solid rgba(124,99,216,0.3)', borderRadius: '10px', color: '#C4B5FD', fontWeight: 600 }}>{label}</Box>
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                    <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.5)', mb: 1.25 }}>Your Institution Receives</Typography>
                    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: '7px', mb: 2 }}>
                      {['Complete Administrative Platform', 'Management & Principal Dashboard', 'Faculty & Employee Workspace', 'Student Information Management', 'Admissions, Academics & Attendance', 'Finance, Reports & Examinations', 'Communication & Notifications', 'Continuous Platform Enhancements'].map(item => (
                        <Box component="li" key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, fontSize: '13px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
                          <Box component="span" sx={{ color: '#A78BFA', fontWeight: 700, flexShrink: 0 }}>→</Box> {item}
                        </Box>
                      ))}
                    </Box>
                    <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', fontStyle: 'italic', lineHeight: 1.6, mb: 2.5 }}>
                      Best suited for institutions that prefer to centrally manage and provide a complete digital ecosystem for every stakeholder.
                    </Typography>
                    <Box component="button" onClick={() => setDemoOpen(true)} sx={{ display: 'inline-block', px: '22px', py: '10px', bgcolor: '#7C63D8', borderRadius: '10px', color: '#fff', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer', fontFamily: '"DM Sans", sans-serif', transition: 'all 0.2s', '&:hover': { bgcolor: '#6B52D1', transform: 'translateY(-1px)' } }}>
                      Book a Demo
                    </Box>
                  </Box>
                )}
              </Box>
            </Reveal>

            {/* Card B — Flexible Partnership (light) */}
            <Reveal delay={0.07}>
              <Box sx={{ p: '36px 32px', borderRadius: '20px', bgcolor: '#FFFFFF', border: '1.5px solid #E5E7EB', height: '100%', display: 'flex', flexDirection: 'column', transition: 'all 0.3s', '&:hover': { borderColor: 'rgba(124,99,216,0.35)', transform: 'translateY(-3px)', boxShadow: '0 16px 48px rgba(0,0,0,0.08)' } }}>
                <Box sx={{ display: 'inline-block', px: '12px', py: '4px', bgcolor: 'rgba(124,99,216,0.08)', border: '1px solid rgba(124,99,216,0.2)', borderRadius: '100px', fontSize: '11px', fontWeight: 600, color: '#7C63D8', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 2, alignSelf: 'flex-start' }}>
                  Flexible Approach
                </Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#7C63D8', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5 }}>Flexible Partnership</Typography>
                <Typography variant="h3" sx={{ fontSize: '21px', color: '#111827', mb: 1.5 }}>Technology That Adapts</Typography>
                <Typography sx={{ fontSize: '14.5px', color: '#475569', lineHeight: 1.65 }}>
                  The institution receives the complete administrative platform while students can activate their own personalized digital experience through web and mobile applications whenever they choose.
                </Typography>
                <Typography sx={{ fontSize: '13.5px', color: '#7C63D8', fontWeight: 600, mt: 2 }}>
                  Perfect for institutions seeking a scalable and flexible adoption model.
                </Typography>
                <Box
                  onClick={() => setHiwOpen(s => ({ ...s, flexible: !s.flexible }))}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', mt: '20px', fontSize: '14px', fontWeight: 600, color: '#7C63D8', cursor: 'pointer', userSelect: 'none', transition: 'gap 0.2s', '&:hover': { gap: '10px' }, alignSelf: 'flex-start' }}
                >
                  How It Works
                  <Box component="span" sx={{ display: 'inline-block', transition: 'transform 0.3s', transform: hiwOpen.flexible ? 'rotate(90deg)' : 'none' }}>→</Box>
                </Box>
                {hiwOpen.flexible && (
                  <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #E5E7EB' }}>
                    <Box sx={{ textAlign: 'center', fontSize: '13px', mb: 3 }}>
                      {[['Institution'], ['Management', 'Faculty', 'Employees'], ['Complete Administrative Platform'], ['Students Activate Personalized Web & Mobile Experience']].map((group, gi) => (
                        <Box key={gi}>
                          {gi > 0 && <Typography sx={{ color: '#94A3B8', fontSize: '18px', my: '4px' }}>↓</Typography>}
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {group.map(label => (
                              <Box key={label} sx={{ px: '20px', py: '8px', bgcolor: '#F3F0FF', border: '1px solid rgba(124,99,216,0.25)', borderRadius: '10px', color: '#7C63D8', fontWeight: 600, textAlign: 'center', lineHeight: 1.4 }}>{label}</Box>
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                    <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94A3B8', mb: 1.25 }}>Institution Receives</Typography>
                    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: '7px', mb: 2 }}>
                      {['Complete Administrative Platform', 'Management & Principal Dashboard', 'Faculty & Employee Workspace', 'Reports & Analytics', 'Continuous Platform Updates'].map(item => (
                        <Box component="li" key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>
                          <Box component="span" sx={{ color: '#7C63D8', fontWeight: 700, flexShrink: 0 }}>→</Box> {item}
                        </Box>
                      ))}
                    </Box>
                    <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94A3B8', mb: 1.25 }}>Student Digital Experience</Typography>
                    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: '7px', mb: 2 }}>
                      {['Attendance & Academic Progress', 'Timetable & Assignments', 'Notifications & Announcements', 'Digital Documents', 'Web & Mobile Access'].map(item => (
                        <Box component="li" key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>
                          <Box component="span" sx={{ color: '#7C63D8', fontWeight: 700, flexShrink: 0 }}>→</Box> {item}
                        </Box>
                      ))}
                    </Box>
                    <Typography sx={{ fontSize: '13px', color: '#94A3B8', fontStyle: 'italic', lineHeight: 1.6, mb: 2.5 }}>
                      Best suited for institutions looking for a flexible digital transformation strategy while delivering a modern connected experience for students.
                    </Typography>
                    <Box component="button" onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })} sx={{ display: 'inline-block', px: '22px', py: '10px', bgcolor: '#7C63D8', borderRadius: '10px', color: '#fff', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer', fontFamily: '"DM Sans", sans-serif', transition: 'all 0.2s', '&:hover': { bgcolor: '#6B52D1', transform: 'translateY(-1px)' } }}>
                      Talk to Our Team
                    </Box>
                  </Box>
                )}
              </Box>
            </Reveal>

          </Box>

          {/* Closing philosophy */}
          <Reveal>
          </Reveal>

        </Container>
      </Box>

      <ExploreSection />

      {/* ── TESTIMONIALS ── */}
      {SHOW_TESTIMONIALS && (
        <Box id="testimonials" sx={{ bgcolor: '#F5F3FF', py: '100px' }}>
          <Container maxWidth="lg">
            <Reveal>
              <Box sx={{ textAlign: 'center', mb: 7 }}>
                <Typography variant="h2" sx={{ fontSize: 'clamp(32px, 4vw, 52px)', color: '#111827', mb: 2.5 }}>
                  Heard From the People<br />Who Use It Every Day
                </Typography>
                <Typography sx={{ fontSize: '18px', color: '#475569', maxWidth: '560px', mx: 'auto', lineHeight: 1.65, fontWeight: 300 }}>
                  Real outcomes from institutions that made the shift to SkolarMinds.
                </Typography>
              </Box>
            </Reveal>

            {/* Carousel window */}
            {(() => {
              const windowSize = isMobile ? 1 : 3;
              const totalPages = Math.ceil(TESTIMONIALS_DATA.length / windowSize);
              const currentPage = Math.floor(testimonialIndex / windowSize);
              const canPrev = currentPage > 0;
              const canNext = currentPage < totalPages - 1;

              const goNext = () => { if (!canNext) return; setTestimonialIndex(currentPage * windowSize + windowSize); };
              const goPrev = () => { if (!canPrev) return; setTestimonialIndex((currentPage - 1) * windowSize); };
              const goDot  = (pageIdx: number) => setTestimonialIndex(pageIdx * windowSize);

              return (
                <Box>
                  {/* Sliding track — all pages live in DOM, track translates */}
                  <Box sx={{ overflow: 'hidden', mb: 5 }}>
                    <MotionBox
                      animate={{ x: `-${currentPage * (100 / totalPages)}%` }}
                      transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                      sx={{ display: 'flex', width: `${totalPages * 100}%` }}
                    >
                      {Array.from({ length: totalPages }).map((_, pageIdx) => (
                        <Box
                          key={pageIdx}
                          sx={{
                            width: `${100 / totalPages}%`, flexShrink: 0,
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)' },
                            gap: '20px',
                          }}
                        >
                          {TESTIMONIALS_DATA.slice(pageIdx * windowSize, (pageIdx + 1) * windowSize).map((t, i) => {
                            const accent = '#7C63D8';

                            return (
                              <Box key={i} sx={{ display: 'flex', flexDirection: 'column' }}>
                                {/* Card */}
                                <Box sx={{
                                  flex: 1,
                                  position: 'relative',
                                  borderRadius: '16px',
                                  bgcolor: '#fff',
                                  boxShadow: '0 4px 20px rgba(124,99,216,0.09)',
                                  border: '1.5px solid rgba(124,99,216,0.14)',
                                  overflow: 'hidden',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                  '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 32px rgba(124,99,216,0.18)' },
                                }}>
                                  {/* Content */}
                                  <Box sx={{ px: '20px', pt: '18px', pb: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    <Typography sx={{
                                      fontFamily: '"Syne", sans-serif', fontWeight: 800,
                                      fontSize: '15px', color: accent, lineHeight: 1.2, mb: 0.4,
                                    }}>
                                      {t.name}
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: '#64748B', mb: 2 }}>
                                      {t.role}
                                    </Typography>
                                    <Typography sx={{ fontSize: '13.5px', color: '#374151', lineHeight: 1.75, flex: 1 }}>
                                      {t.quote}
                                    </Typography>
                                    {/* Stars */}
                                    <Box sx={{ display: 'flex', gap: '4px', mt: 2.5 }}>
                                      {[1,2,3,4,5].map(n => (
                                        <Star key={n} size={17} fill="#F59E0B" color="#F59E0B" />
                                      ))}
                                    </Box>
                                  </Box>

                                  {/* Decorative quote mark — bottom right */}
                                  <Typography sx={{
                                    position: 'absolute', bottom: 6, right: 12,
                                    fontSize: '56px', lineHeight: 1,
                                    color: '#7C63D8',
                                    fontFamily: 'Georgia, "Times New Roman", serif',
                                    userSelect: 'none', pointerEvents: 'none',
                                  }}>
                                    &#x275E;
                                  </Typography>
                                </Box>

                              </Box>
                            );
                          })}
                        </Box>
                      ))}
                    </MotionBox>
                  </Box>

                  {/* Navigation */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    <Box
                      onClick={goPrev}
                      sx={{
                        width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: '1.5px solid', transition: 'all 0.2s',
                        borderColor: canPrev ? '#E2E8F0' : '#F1F5F9',
                        color: canPrev ? '#475569' : '#CBD5E1',
                        cursor: canPrev ? 'pointer' : 'default',
                        '&:hover': canPrev ? { borderColor: '#7C63D8', color: '#7C63D8', bgcolor: '#F3F0FF' } : {},
                      }}
                    >
                      <ChevronLeft size={18} />
                    </Box>

                    {/* Page dots */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {Array.from({ length: totalPages }).map((_, pageIdx) => (
                        <Box
                          key={pageIdx}
                          onClick={() => goDot(pageIdx)}
                          sx={{
                            width: pageIdx === currentPage ? 20 : 8,
                            height: 8, borderRadius: '100px', cursor: 'pointer', transition: 'all 0.3s',
                            bgcolor: pageIdx === currentPage ? '#7C63D8' : '#E2E8F0',
                          }}
                        />
                      ))}
                    </Box>

                    <Box
                      onClick={goNext}
                      sx={{
                        width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: '1.5px solid', transition: 'all 0.2s',
                        borderColor: canNext ? '#E2E8F0' : '#F1F5F9',
                        color: canNext ? '#475569' : '#CBD5E1',
                        cursor: canNext ? 'pointer' : 'default',
                        '&:hover': canNext ? { borderColor: '#7C63D8', color: '#7C63D8', bgcolor: '#F3F0FF' } : {},
                      }}
                    >
                      <ChevronRight size={18} />
                    </Box>
                  </Box>
                </Box>
              );
            })()}
          </Container>
        </Box>
      )}

      {/* ── STATS ── */}
      <Box sx={{ bgcolor: '#0F172A', py: '64px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', gap: { xs: 4, md: 6 }, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
            {[
              { num: '5', sup: '+', label: 'Institutions' },
              { num: '5000', sup: '+', label: 'Students Empowered' },
              { num: '21', sup: '+', label: 'Integrated Modules' },
              { num: '98', sup: '%', label: 'Satisfaction Rate' },
            ].map((s, i) => (
              <React.Fragment key={s.label}>
                {i > 0 && <Box sx={{ width: '1px', height: '48px', bgcolor: 'rgba(255,255,255,0.12)', display: { xs: 'none', sm: 'block' } }} />}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '36px', fontWeight: 800, color: '#fff', lineHeight: 1 }}>
                    {s.num}<Box component="span" sx={{ color: '#7C63D8' }}>{s.sup}</Box>
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)', mt: 0.75, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{s.label}</Typography>
                </Box>
              </React.Fragment>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── FOOTER ── */}
      <Box component="footer" id="contact" sx={{ bgcolor: '#0A0E17', pt: '64px', pb: '32px', color: 'rgba(255,255,255,0.5)' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} sx={{ pb: 6, borderBottom: '1px solid rgba(255,255,255,0.06)', mb: 4 }}>

            {/* Brand + contact block */}
            <Grid item xs={12} md={4}>
              <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '24px', fontWeight: 800, color: '#fff', mb: 1 }}>
                Skolar<Box component="span" sx={{ color: '#7C63D8' }}>Minds</Box>
              </Typography>
              <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.35)', mb: 2.5, lineHeight: 1.5 }}>
                End-to-End Educational Institution Management Platform
              </Typography>
              <Typography sx={{ fontSize: '13.5px', color: 'rgba(255,255,255,0.4)', lineHeight: 1.8, mb: 3, maxWidth: '300px' }}>
                SkolarMinds helps educational institutions operate smarter — connecting every stakeholder through one unified platform.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Typography sx={{ fontSize: '16px', mt: '1px', flexShrink: 0 }}>📍</Typography>
                  <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>
                    No.2532, Station-6, Aliens Space Station,<br />
                    Tellapur, Hyderabad — 502034<br />
                    India
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '16px', flexShrink: 0 }}>📞</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <Box component="a" href="tel:+918977600601" sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', '&:hover': { color: '#fff' }, transition: 'color 0.2s' }}>
                      8977600601
                    </Box>
                    <Box component="a" href="tel:+917036241412" sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', '&:hover': { color: '#fff' }, transition: 'color 0.2s' }}>
                      70362 41412
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '16px', flexShrink: 0 }}>✉️</Typography>
                  <Box component="a" href="mailto:contact@skolarminds.com" sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', '&:hover': { color: '#fff' }, transition: 'color 0.2s' }}>
                    contact@skolarminds.com
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Nav link columns */}
            {[
              { heading: 'Platform', links: [
                { label: 'All Modules', href: '#modules' },
                { label: 'Analytics & Reports', href: '#modules' },
                { label: 'Role Experiences', href: '#roles' },
                { label: 'Overview', href: '#vision' },
              ]},
              { heading: 'Institutions', links: [
                { label: 'Schools', href: '#roles' },
                { label: 'Colleges', href: '#roles' },
                { label: 'Multi-Campus Groups', href: '#roles' },
                { label: 'Educational Trusts', href: '#roles' },
              ]},
              { heading: 'Legal', links: [
                { label: 'Terms & Conditions', href: '/terms-and-conditions' },
                { label: 'Privacy Policy', href: '/privacy-policy' },
              ]},
            ].map(col => (
              <Grid item xs={6} md={2} key={col.heading}>
                <Typography sx={{ fontFamily: '"Syne", sans-serif', fontSize: '13px', fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 2.5 }}>
                  {col.heading}
                </Typography>
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {col.links.map(l => (
                    <Box component="li" key={l.label}>
                      <Box component="a" href={l.href} sx={{ fontSize: '13.5px', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', transition: 'color 0.2s', '&:hover': { color: 'rgba(255,255,255,0.8)' } }}>
                        {l.label}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Grid>
            ))}

          </Grid>

          {/* Bottom bar */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.2)' }}>
              © 2026 SkolarMinds. All rights reserved.
            </Typography>
            <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.2)' }}>
              Built for Indian educational institutions.
            </Typography>
          </Box>
        </Container>
      </Box>

      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </Box>
  );
}

// ─── HOME ─────────────────────────────────────────────────────────────────────
export function Home() {
  return (
    <ThemeProvider theme={homeTheme}>
      <HomeInner />
    </ThemeProvider>
  );
}

export default Home;

